import qs from 'qs';

const axios = require('axios');
const validate = require('jsonschema').validate;

/** Base class handling common functions of the api */
class HusleieAPI2 {

	/**
	 * Creates a new instance of HusleieAPI2
	 *
	 * @return {HusleieAPI2} The new HusleieAPI2 object
	 */
	constructor() {

		if ('NODE_ENV' in process.env && process.env.NODE_ENV === 'development') {
			if (!('VUE_APP_API_BASE_URL' in process.env) || !('VUE_APP_AUTH_URL' in process.env)) {
				this.logger('error', 'Your are running this app as developer, but are still talking to the production servers. (Hint: Add your own .env.* file in the project, or just ask Kay');
			}
		}

		let apiKeyKeyPrefix = ('VUE_APP_API_KEY_KEY_PREFIX' in process.env) ? process.env.VUE_APP_API_KEY_KEY_PREFIX : '___api_key_husleie_api_v2_';
		/** @private */ this.baseUrl = ('VUE_APP_API_BASE_URL' in process.env) ? process.env.VUE_APP_API_BASE_URL : 'https://api.husleie.no/api/v2';
		/** @private */ this.authUrl = ('VUE_APP_AUTH_URL' in process.env) ? process.env.VUE_APP_AUTH_URL : 'www.husleie.no/api/jwt';

		/** Add posibility to override API_BASE_URL of static built apps **/
		if ( window.localStorage.getItem('__OVERRIDE_API_BASE_URL') )
			this.baseUrl = window.localStorage.getItem('__OVERRIDE_API_BASE_URL');

		/** Add posibility to override AUTH_URL of static built apps **/
		if ( window.localStorage.getItem('__OVERRIDE_AUTH_URL') )
			this.authUrl = window.localStorage.getItem('__OVERRIDE_AUTH_URL');

		/** Normalize API key name **/
		/** @private */ this.apiKeyKey = apiKeyKeyPrefix + this.authUrl;
		this.apiKeyKey = this.apiKeyKey.replace(/[./]+/g, '_');
		this.apiKeyKey = this.apiKeyKey.replace(/\W/g, '');

		/** @private */ this.axios = axios.create({
			baseURL: this.baseUrl,
			withCredentials: true,
			transformRequest: [(data, headers) => {
				try {
					if ('localStorage' in window) {
						let token = window.localStorage.getItem(this.apiKeyKey);
						if (token) {
							headers['Authorization'] = token;
						}
					}
				} catch(e) {
					this.logger('error', 'In transformRequest', e);
				}

				// Force the request to be application/json
				headers['Content-Type'] = 'application/json;charset=utf-8';
				return JSON.stringify(data);
			}],
		});

		/** @private */ this.validate = validate;
	}

	/**
	 * Expands variables in a path containing {} templates
	 *
	 * @private
	 * @param {string} path The path string to be expanded
	 * @param {object} vals The object containing the values to be used when expanding
	 * @return {string} The expanded path
	 */
	expandPath(path, vals) {
		const regex = /\{(.*?)\}/;
		let match;
		while ((match = regex.exec(path)) !== null) {
			if (match[1] in vals && vals[match[1]] && vals[match[1]] !== '') {
				path = path.replace(match[0], vals[match[1]]);
			} else {
				let err = `Could not expand ${match[1]} in ${path}`;
				this.logger('error', err);
				throw new Error(err);
			}
		}
		return path;
	}

	/**
	 * Deauthorize the client by removing the cached APIKey. The client will try to reauthorize on the next request.
	 *
	 * @private
	 * @return {void}
	 */
	deauthorize() {
		try {
			if ('localStorage' in window) {
				window.localStorage.removeItem(this.apiKeyKey);
			}
		} catch(e) {
			this.logger('error', e);
		}
	}

	/**
	 * Asks auth server for a new API token
	 *
	 * @private
	 * @return {Promise}
	 */
	fetchAPIToken(token) {
		return new Promise((resolve, reject) => {
			axios.create({
				withCredentials: true,
			}).post(this.authUrl, token ? { token } : {}).then( res => {

				let token = 'Bearer ' + res.data.token;

				try {
					if ('localStorage' in window) {
						window.localStorage.setItem(this.apiKeyKey, token);
					}
				} catch(e) {
					this.logger('error', e);
				}

				resolve();

			}).catch( err => { reject(err); });
		});
	}

	/**
	 * Provides retry mechanism and handling of 401 responses from the API server
	 *
	 * @private
	 * @param {function} fn The function, returning a Promise, that should be retried.
	 * @param {number} retries The number of times the function should max be retried. Default 3.
	 * @param {object} err An error object. Should only be provided by the function itself.
	 * @return {Promise}
	 */
	retry(fn, retries=3, err=null) {

		if (!retries)
			return Promise.reject(err);

		return fn().catch(err => {
			if (err && err.response && (err.response.status == 401 || err.response.status == 403)) {
				this.logger('Got 401 from API, requesting new API token...');
				return this.fetchAPIToken().then( () => {
					this.logger('Got new token from API, retrying the original request.');
					return this.retry(fn, (retries-1), err);
				}).catch( err => {
					this.logger('error', 'Got error when asking for a new API token', err);
					return Promise.reject(err);
				});
			} else if (err && err.response && ( (err.response.status >= 500 && err.response.status <= 599) || err.response.status === '400' || err.response.status === '404' )) {
				this.logger('error', 'Got error for API server', err);
				return Promise.reject(err);
			} else if (err && err.isAxiosError) {
				this.logger('error', 'Got error from Axios:', err.toString());
				return Promise.reject(err);
			}

			this.logger('Retrying API request, ', (retries-1), ' retries remainding...');
			return this.retry(fn, (retries-1), err);
		});
	}

	/**
	 * Validates an object against a jsonschema
	 *
	 * @private
	 * @param {object} data The object to be validated
	 * @param {object} schema The schema that data should be validated against
	 * @return {boolean}
	 */
	validateSchema(data, schema) {
		const v = this.validate(data, schema);

		if (!v.valid) {
			this.logger('error', 'body does not validate:');
			for(let i = 0; i<v.errors.length; i++) {
				this.logger('error', ' -', v.errors[i].stack, v.errors[i]);
			}
		}

		return v.valid;
	}

	/**
	 * Provides a logger
	 *
	 * @private
	 * @param {...(boolean|number|string|object)} Any number of arguments to be logged on the same line. Objects will be copied, and logged as is at the time of logging.
	 */
	logger(...args) {
		if (!window.___api_logging)
			return;

		let level = 'info';

		const levels = {
			info: [ '%cAPI%c %c', 'color:blue;padding:2px 4px 2px 4px;border-radius:5px;background-color:#eef;border:.5px solid blue;', '', '' ],
			error: [ '%cAPI ERROR%c %c', 'color:red;padding:2px 4px 2px 4px;border-radius:5px;background-color:#fee;border:.5px solid red;', '', '' ],
			warn: [ '%cAPI WARN%c %c', 'color:orange;padding:2px 4px 2px 4px;border-radius:5px;background-color:#ffe;border:.5px solid orange;', '', '' ],
			debug: [ '%cAPI DEBUG%c %c', 'color:green;padding:2px 4px 2px 4px;border-radius:5px;background-color:#efe;border:.5px solid green;', '', '' ],
		};

		if (args[0] === 'error' || args[0] === 'warn' || args[0] === 'debug') {
			level = args.shift();
		}

		let template = [];
		for(let i=0;i<args.length;i++) {
			if (typeof(args[i]) === 'string' || typeof(args[i]) === 'undefined') {
				template.push('%s');
			} else if (typeof(args[i]) === 'number') {
				template.push( (Number(args[i]) === args[i] && args[i] % 1 !== 0) ? '%f' : '%d' );
			} else {
				args[i] = JSON.parse(JSON.stringify(args[i]));
				template.push('%O');
			}
		}
		// eslint-disable-next-line no-console
		console.log( levels[level].shift() + template.join(' '), ...levels[level], ...args);
	}

}


/**
 * Address
 * @extends HusleieAPI2
 */
export class AddressApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Address"...');

	}


	/**
	 * Attempts to find the name of the area a postal code refers to in a country
	 * @param {} country The two-letter country code this postcode belongs to
	 * @param {} code The postcode we are looking up
	 * @see {@link file:///./redoc-husleieapi.html#operation/getPostalArea}
	 * @returns {Promise} A promise
	 */
	getPostalArea(country, code, responseType = 'json') {

		this.logger('Calling API function "getPostalArea" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/postalarea/{country}/{code}', {
			country,
			code,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Chat
 * @extends HusleieAPI2
 */
export class ChatApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Chat"...');

	}


	/**
	 * Adds feedback to a chat session.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of the chat session.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.rating 

	 * @see {@link file:///./redoc-husleieapi.html#operation/chatSessionFeedback}
	 * @returns {Promise} A promise
	 */
	chatSessionFeedback(landlord_id, chat_session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "chatSessionFeedback" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"rating":{"maximum":5,"minimum":1,"type":"number"}},"required":["rating"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}/feedback', {
			landlord_id,
			chat_session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new chat session.
	 * @param {} landlord_id The ID of a landlord.
	 * @see {@link file:///./redoc-husleieapi.html#operation/createChatSession}
	 * @returns {Promise} A promise
	 */
	createChatSession(landlord_id, responseType = 'json') {

		this.logger('Calling API function "createChatSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/chat', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Deletes a chat session.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of the chat session.
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteChatSession}
	 * @returns {Promise} A promise
	 */
	deleteChatSession(landlord_id, chat_session_id, responseType = 'json') {

		this.logger('Calling API function "deleteChatSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}', {
			landlord_id,
			chat_session_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns a chat session object.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of the chat session.
	 * @see {@link file:///./redoc-husleieapi.html#operation/getChatSession}
	 * @returns {Promise} A promise
	 */
	getChatSession(landlord_id, chat_session_id, responseType = 'json') {

		this.logger('Calling API function "getChatSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}', {
			landlord_id,
			chat_session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Adds a message to a chat session from a user.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of the chat session.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.message 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postMessageToChatSession}
	 * @returns {Promise} A promise
	 */
	postMessageToChatSession(landlord_id, chat_session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postMessageToChatSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"message":{"type":"string"}},"required":["message"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}', {
			landlord_id,
			chat_session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Updates the status of a component in a chat session.
	 * @param {} landlord_id The ID of a landlord.
	 * @param {} chat_session_id The ID of a chat session.
	 * @param {} component_id The ID of a component.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.status  Enum: ['accepted', 'declined']

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateStatusForChatSessionComponent}
	 * @returns {Promise} A promise
	 */
	updateStatusForChatSessionComponent(landlord_id, chat_session_id, component_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateStatusForChatSessionComponent" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"status":{"enum":["accepted","declined"],"type":"string"}},"required":["status"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/chat/{chat_session_id}/component/{component_id}', {
			landlord_id,
			chat_session_id,
			component_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Contract
 * @extends HusleieAPI2
 */
export class ContractApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Contract"...');

	}


	/**
	 * Generates and offers the contract as a PDF-file for download. See download_token for generating the required token.
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.token {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/contract_download}
	 * @returns {Promise} A promise
	 */
	contract_download(contract_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "contract_download" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/contract_session/{contract_id}/download', {
			contract_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get a preview of the contract, based on the information in the contract session
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.format {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/contract_preview}
	 * @returns {Promise} A promise
	 */
	contract_preview(contract_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "contract_preview" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/contract_session/{contract_id}/preview', {
			contract_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new contract session object that the client can use to store contract session information.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.interest_management_id 
	 * @param {number} data.body.rental_object_id 
	 * @param {number} data.body.tenancy_id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createContractSession}
	 * @returns {Promise} A promise
	 */
	createContractSession({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createContractSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"interest_management_id":{"type":"string"},"rental_object_id":{"type":"number"},"tenancy_id":{"type":"number"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/contract_session';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Delete a contract
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteContractSession}
	 * @returns {Promise} A promise
	 */
	deleteContractSession(contract_id, responseType = 'json') {

		this.logger('Calling API function "deleteContractSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/contract_session/{contract_id}', {
			contract_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the content of a contract session
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getContractSession}
	 * @returns {Promise} A promise
	 */
	getContractSession(contract_id, responseType = 'json') {

		this.logger('Calling API function "getContractSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/contract_session/{contract_id}', {
			contract_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * This method will cancel the signing process and send the contract back to the landlord
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.message 

	 * @see {@link file:///./redoc-husleieapi.html#operation/sendContractFeedback}
	 * @returns {Promise} A promise
	 */
	sendContractFeedback(contract_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "sendContractFeedback" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"message":{"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/contract_session/{contract_id}/feedback', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * This method will start the signing procedure by sending tenant a mail with a link for signing the contract
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 
	 * @param {object} data.body.callbacks 
	 * @param {object} data.body.callbacks.landlord 
	 * @param {string} data.body.callbacks.landlord.cancel 
	 * @param {string} data.body.callbacks.landlord.completed 
	 * @param {string} data.body.callbacks.landlord.error 
	 * @param {string} data.body.callbacks.landlord.postpone 
	 * @param {object} data.body.callbacks.tenant 
	 * @param {string} data.body.callbacks.tenant.cancel 
	 * @param {string} data.body.callbacks.tenant.completed 
	 * @param {string} data.body.callbacks.tenant.error 
	 * @param {string} data.body.callbacks.tenant.postpone 
	 * @param {string} data.body.signingMethod  Enum: ['bankid-no', 'canvas', 'offline', 'verified_digital_signing'] Default: [bankid-no]

	 * @see {@link file:///./redoc-husleieapi.html#operation/sendContractForSigning}
	 * @returns {Promise} A promise
	 */
	sendContractForSigning(contract_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "sendContractForSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"callbacks":{"properties":{"landlord":{"properties":{"cancel":{"type":"string"},"completed":{"type":"string"},"error":{"type":"string"},"postpone":{"type":"string"}},"required":["completed","postpone","cancel","error"],"type":"object"},"tenant":{"properties":{"cancel":{"type":"string"},"completed":{"type":"string"},"error":{"type":"string"},"postpone":{"type":"string"}},"required":["completed","postpone","cancel","error"],"type":"object"}},"required":["tenant","landlord"],"type":"object"},"signingMethod":{"default":"bankid-no","enum":["bankid-no","canvas","offline","verified_digital_signing"],"type":"string"}}};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/contract_session/{contract_id}/send_for_signing', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Update a contract session, replacing the content of the contract session
	 * @param {} contract_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.dummy 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateContractSession}
	 * @returns {Promise} A promise
	 */
	updateContractSession(contract_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateContractSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"dummy":{"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/contract_session/{contract_id}', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * ContractSigning
 * @extends HusleieAPI2
 */
export class ContractSigningApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "ContractSigning"...');

	}


	/**
	 * Cancel a contract signing before landlord has signed
	 * @param {} contract_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/cancelContractSigning}
	 * @returns {Promise} A promise
	 */
	cancelContractSigning(contract_id, responseType = 'json') {

		this.logger('Calling API function "cancelContractSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/contract_signing/{contract_id}/cancel', {
			contract_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * DepositSubscription
 * @extends HusleieAPI2
 */
export class DepositSubscriptionApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "DepositSubscription"...');

	}


	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createApplication}
	 * @returns {Promise} A promise
	 */
	createApplication({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createApplication" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/product/deposit_subscription/';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getApproval}
	 * @returns {Promise} A promise
	 */
	getApproval(id, responseType = 'json') {

		this.logger('Calling API function "getApproval" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/product/deposit_subscription/{id}/approval', {
			id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @see {@link file:///./redoc-husleieapi.html#operation/initiatePayment}
	 * @returns {Promise} A promise
	 */
	initiatePayment(id, responseType = 'json') {

		this.logger('Calling API function "initiatePayment" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/product/deposit_subscription/{id}/initiate_payment', {
			id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchApplication}
	 * @returns {Promise} A promise
	 */
	patchApplication(id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchApplication" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/product/deposit_subscription/{id}', {
			id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/submitApplication}
	 * @returns {Promise} A promise
	 */
	submitApplication(id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "submitApplication" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/product/deposit_subscription/{id}', {
			id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.decision  Enum: ['accepted', 'declined']
	 * @param {object} data.body.landlord 
	 * @param {string} data.body.landlord.address 
	 * @param {string} data.body.landlord.email 
	 * @param {string} data.body.landlord.familyName 
	 * @param {string} data.body.landlord.givenName 
	 * @param {boolean} data.body.landlord.isCompany 
	 * @param {string} data.body.landlord.organizationName 
	 * @param {string} data.body.landlord.organizationNumber 
	 * @param {string} data.body.landlord.phoneNumber 
	 * @param {string} data.body.landlord.postalArea 
	 * @param {string} data.body.landlord.postalCode 
	 * @param {object} data.body.tenancy 
	 * @param {string} data.body.tenancy.address 
	 * @param {boolean} data.body.tenancy.areTermsAccepted 
	 * @param {boolean} data.body.tenancy.isPrivacyPolicyAccepted 
	 * @param {string} data.body.tenancy.postalArea 
	 * @param {string} data.body.tenancy.postalCode 
	 * @param {string} data.body.tenancy.startDate 

	 * @see {@link file:///./redoc-husleieapi.html#operation/submitApprovalDecision}
	 * @returns {Promise} A promise
	 */
	submitApprovalDecision(id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "submitApprovalDecision" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"decision":{"enum":["accepted","declined"],"type":"string"},"landlord":{"properties":{"address":{"type":"string"},"email":{"type":"string"},"familyName":{"type":"string"},"givenName":{"type":"string"},"isCompany":{"type":"boolean"},"organizationName":{"type":"string"},"organizationNumber":{"type":"string"},"phoneNumber":{"type":"string"},"postalArea":{"type":"string"},"postalCode":{"type":"string"}},"type":"object"},"tenancy":{"properties":{"address":{"type":"string"},"areTermsAccepted":{"type":"boolean"},"isPrivacyPolicyAccepted":{"type":"boolean"},"postalArea":{"type":"string"},"postalCode":{"type":"string"},"startDate":{"type":"string"}},"type":"object"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/product/deposit_subscription/{id}/approval', {
			id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Electricity
 * @extends HusleieAPI2
 */
export class ElectricityApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Electricity"...');

	}


	/**
	 *
	 * @param {} rental_object_id
	 * @param {} token
	 * @see {@link file:///./redoc-husleieapi.html#operation/getMeterAppData}
	 * @returns {Promise} A promise
	 */
	getMeterAppData(rental_object_id, token, responseType = 'json') {

		this.logger('Calling API function "getMeterAppData" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/product/electricity/meter/{rental_object_id}/{token}', {
			rental_object_id,
			token,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} rental_object_id
	 * @param {} token
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.electricityMeterNumber Electricity meter number

	 * @see {@link file:///./redoc-husleieapi.html#operation/setElectricityMeter}
	 * @returns {Promise} A promise
	 */
	setElectricityMeter(rental_object_id, token, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "setElectricityMeter" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"electricityMeterNumber":{"description":"Electricity meter number","type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/product/electricity/meter/{rental_object_id}/{token}', {
			rental_object_id,
			token,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.electricityMeterNumber Meter ID or meter number
	 * @param {integer} data.body.rentalObjectId Rental Object Id

	 * @see {@link file:///./redoc-husleieapi.html#operation/verifyElectricityMeterData}
	 * @returns {Promise} A promise
	 */
	verifyElectricityMeterData({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "verifyElectricityMeterData" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"electricityMeterNumber":{"description":"Meter ID or meter number","type":"string"},"rentalObjectId":{"description":"Rental Object Id","type":"integer"}},"required":["electricityMeterNumber"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/product/electricity/verify_meter_data';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Identification
 * @extends HusleieAPI2
 */
export class IdentificationApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Identification"...');

	}


	/**
	 * Create a new identification session
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.language  Enum: ['no', 'en', 'sv', 'da', 'fi'] Default: [no]
	 * @param {string} data.body.path 
	 * @param {string} data.body.prefillSSN 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createIdentificationSession}
	 * @returns {Promise} A promise
	 */
	createIdentificationSession({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createIdentificationSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"language":{"default":"no","enum":["no","en","sv","da","fi"],"type":"string"},"path":{"type":"string"},"prefillSSN":{"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/identification';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the result of the identification session, if any.
	 * @param {} identification_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getIdentificationSessionResult}
	 * @returns {Promise} A promise
	 */
	getIdentificationSessionResult(identification_id, responseType = 'json') {

		this.logger('Calling API function "getIdentificationSessionResult" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/identification/{identification_id}', {
			identification_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Invalidate a identification session to avoid reuse
	 * @param {} identification_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/invalidateIdentificationSession}
	 * @returns {Promise} A promise
	 */
	invalidateIdentificationSession(identification_id, responseType = 'json') {

		this.logger('Calling API function "invalidateIdentificationSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/identification/{identification_id}', {
			identification_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * InterestManagement
 * @extends HusleieAPI2
 */
export class InterestManagementApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "InterestManagement"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordInterestManagement}
	 * @returns {Promise} A promise
	 */
	getLandlordInterestManagement(landlord_id, interest_management_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordInterestManagement" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}', {
			landlord_id,
			interest_management_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordInterestManagementCandidates}
	 * @returns {Promise} A promise
	 */
	getLandlordInterestManagementCandidates(landlord_id, interest_management_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordInterestManagementCandidates" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates', {
			landlord_id,
			interest_management_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.rentalObjectId {}Return only projects related to this rental object
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordInterestManagements}
	 * @returns {Promise} A promise
	 */
	getLandlordInterestManagements(landlord_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordInterestManagements" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/interest_managements', {
			landlord_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.adId Ad UUID to be associated with, if set to null it will become standalone
	 * @param {integer} data.body.rentalObjectId If project should be moved to another rental object
	 * @param {string} data.body.title Title to be set, if not present it will be ignored

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchLandlordInterestManagement}
	 * @returns {Promise} A promise
	 */
	patchLandlordInterestManagement(landlord_id, interest_management_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchLandlordInterestManagement" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"adId":{"description":"Ad UUID to be associated with, if set to null it will become standalone","nullable":true,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"rentalObjectId":{"description":"If project should be moved to another rental object","type":"integer"},"title":{"description":"Title to be set, if not present it will be ignored","nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}', {
			landlord_id,
			interest_management_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.ad 
	 * @param {string} data.body.ad.id UUID of associated Ad
	 * @param {string} data.body.id UUID of the Interest Management Project
	 * @param {integer} data.body.rentalObjectId RentalObject.ID the IM Project has been created on
	 * @param {string} data.body.title Title of the Project, if created by ad this would be null

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordInterestManagement}
	 * @returns {Promise} A promise
	 */
	postLandlordInterestManagement(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordInterestManagement" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"ad":{"properties":{"id":{"description":"UUID of associated Ad","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"}},"type":"object"},"id":{"description":"UUID of the Interest Management Project","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"rentalObjectId":{"description":"RentalObject.ID the IM Project has been created on","nullable":false,"type":"integer"},"title":{"description":"Title of the Project, if created by ad this would be null","nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.application 
	 * @param {string} data.body.application.introduction Introducationary text about the applicant
	 * @param {integer} data.body.application.numberOfTenants Number of people living together Default: [0]
	 * @param {string} data.body.application.tenantProof Tenant Proofing reference
	 * @param {array} data.body.comments 
	 * @param {string} data.body.comments[].author Name of the commentor
	 * @param {integer} data.body.comments[].author_id ID of the commentor
	 * @param {string} data.body.comments[].comment The main body of the comment
	 * @param {object} data.body.comments[].date 
	 * @param {string} data.body.comments[].date.created The UTC date and time of when comment were created
	 * @param {string} data.body.comments[].date.edited The UTC date and time of when comment were updated
	 * @param {string} data.body.comments[].id UUID of the comment
	 * @param {string} data.body.created ISO formated date when applicant was created
	 * @param {object} data.body.creditCheck 
	 * @param {string} data.body.creditCheck.comment If candidate left a comment to any of the remarks
	 * @param {string} data.body.creditCheck.expires Date when invitation expires, not to be used when completed
	 * @param {object} data.body.creditCheck.result 
	 * @param {integer} data.body.creditCheck.result.age Age of person in the result
	 * @param {string} data.body.creditCheck.result.dateOfBirth Date of birth in the result, this might not be ISO formated!
	 * @param {string} data.body.creditCheck.result.name The name from the result
	 * @param {integer} data.body.creditCheck.result.numberOfRemarks The total number of remarks found in credit check
	 * @param {integer} data.body.creditCheck.result.score The score that came with the credit check
	 * @param {array} data.body.creditCheck.result.taxYears An array with the years of the last 3 years of registered income
	 * @param {boolean} data.body.creditCheck.shared Boolean flag if result is shared or not, when completed Default: [0]
	 * @param {string} data.body.creditCheck.status Status of the credit check Enum: ['invited', 'completed']
	 * @param {string} data.body.creditCheck.when Date when the credit check was completed
	 * @param {string} data.body.email email address for the candidate
	 * @param {integer} data.body.id ID of the Interest Management Candidate
	 * @param {array} data.body.messages 
	 * @param {string} data.body.messages[].author The name of the sender
	 * @param {string} data.body.messages[].date UTC time of when message was created
	 * @param {integer} data.body.messages[].id Message part ID
	 * @param {string} data.body.messages[].last_read Last read timestamp
	 * @param {string} data.body.messages[].message The message body
	 * @param {string} data.body.messages[].sender Who sent the message Enum: ['landlord', 'tenant', 'candidate', 'system']
	 * @param {string} data.body.name Name of the candidate
	 * @param {string} data.body.phone Phone number for the candidate
	 * @param {integer} data.body.rank The rank of the candidate
	 * @param {array} data.body.references 
	 * @param {string} data.body.references[].category Who is the reference from Enum: ['employer', 'landlord', 'family'] Default: [landlord]
	 * @param {string} data.body.references[].comment If landlord has a comment to the reference check
	 * @param {object} data.body.references[].date 
	 * @param {string} data.body.references[].date.end Date (not ISO, but string) of when the relation ended
	 * @param {string} data.body.references[].date.start Date (not ISO, but string) of when the relation started
	 * @param {string} data.body.references[].email email address for the reference
	 * @param {string} data.body.references[].id UUID of the comment
	 * @param {string} data.body.references[].name Name of the reference
	 * @param {string} data.body.references[].phone Phone number for the reference
	 * @param {boolean} data.body.references[].rating Boolean if reference check is good or bad
	 * @param {string} data.body.status Status of application Enum: ['applicant', 'withdrawn', 'rejected', 'contracted'] Default: [applicant]

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordInterestManagementCandidate}
	 * @returns {Promise} A promise
	 */
	postLandlordInterestManagementCandidate(landlord_id, interest_management_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordInterestManagementCandidate" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"application":{"properties":{"introduction":{"description":"Introducationary text about the applicant","nullable":false,"type":"string"},"numberOfTenants":{"default":"0","description":"Number of people living together","nullable":false,"type":"integer"},"tenantProof":{"description":"Tenant Proofing reference","nullable":true,"pattern":"^\\d{8}\\/\\d\\/\\w{6}$","type":"string"}},"type":"object"},"comments":{"items":{"properties":{"author":{"description":"Name of the commentor","nullable":false,"readOnly":true,"type":"string"},"author_id":{"description":"ID of the commentor","nullable":false,"readOnly":true,"type":"integer"},"comment":{"description":"The main body of the comment","nullable":false,"type":"string"},"date":{"properties":{"created":{"description":"The UTC date and time of when comment were created","format":"date-time","nullable":false,"type":"string"},"edited":{"description":"The UTC date and time of when comment were updated","format":"date-time","nullable":true,"type":"string"}},"readOnly":true,"type":"object"},"id":{"description":"UUID of the comment","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","readOnly":true,"type":"string"}},"type":"object"},"readOnly":true,"type":"array"},"created":{"description":"ISO formated date when applicant was created","format":"date","readOnly":true,"type":"string"},"creditCheck":{"properties":{"comment":{"description":"If candidate left a comment to any of the remarks","nullable":true,"type":"string"},"expires":{"description":"Date when invitation expires, not to be used when completed","format":"date","nullable":false,"type":"string"},"result":{"properties":{"age":{"description":"Age of person in the result","nullable":true,"type":"integer"},"dateOfBirth":{"description":"Date of birth in the result, this might not be ISO formated!","nullable":true,"type":"string"},"name":{"description":"The name from the result","nullable":true,"type":"string"},"numberOfRemarks":{"description":"The total number of remarks found in credit check","nullable":true,"type":"integer"},"score":{"description":"The score that came with the credit check","nullable":true,"type":"integer"},"taxYears":{"description":"An array with the years of the last 3 years of registered income","items":{"description":"The year of income","nullable":false,"type":"integer"},"type":"array"}},"type":"object"},"shared":{"default":false,"description":"Boolean flag if result is shared or not, when completed","nullable":true,"type":"boolean"},"status":{"description":"Status of the credit check","enum":["invited","completed"],"nullable":true,"type":"string"},"when":{"description":"Date when the credit check was completed","format":"date","nullable":true,"type":"string"}},"readOnly":true,"type":"object"},"email":{"description":"email address for the candidate","format":"email","nullable":false,"type":"string"},"id":{"description":"ID of the Interest Management Candidate","readOnly":true,"type":"integer"},"messages":{"items":{"properties":{"author":{"description":"The name of the sender","nullable":false,"type":"string"},"date":{"description":"UTC time of when message was created","format":"date-time","nullable":false,"type":"string"},"id":{"description":"Message part ID","nullable":false,"type":"integer"},"last_read":{"description":"Last read timestamp","format":"date-time","nullable":true,"type":"string"},"message":{"description":"The message body","nullable":false,"type":"string"},"sender":{"description":"Who sent the message","enum":["landlord","tenant","candidate","system"],"type":"string"}},"type":"object"},"readOnly":true,"type":"array"},"name":{"description":"Name of the candidate","nullable":false,"type":"string"},"phone":{"description":"Phone number for the candidate","nullable":true,"type":"string"},"rank":{"description":"The rank of the candidate","maximum":5,"minimum":0,"nullable":true,"type":"integer"},"references":{"items":{"properties":{"category":{"default":"landlord","description":"Who is the reference from","enum":["employer","landlord","family"],"type":"string"},"comment":{"description":"If landlord has a comment to the reference check","nullable":true,"type":"string"},"date":{"properties":{"end":{"description":"Date (not ISO, but string) of when the relation ended","nullable":true,"type":"string"},"start":{"description":"Date (not ISO, but string) of when the relation started","nullable":true,"type":"string"}},"readOnly":true,"type":"object"},"email":{"description":"email address for the reference","format":"email","nullable":true,"type":"string"},"id":{"description":"UUID of the comment","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","readOnly":true,"type":"string"},"name":{"description":"Name of the reference","nullable":false,"type":"string"},"phone":{"description":"Phone number for the reference","nullable":true,"type":"string"},"rating":{"description":"Boolean if reference check is good or bad","nullable":true,"type":"boolean"}},"type":"object"},"type":"array"},"status":{"default":"applicant","description":"Status of application","enum":["applicant","withdrawn","rejected","contracted"],"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates', {
			landlord_id,
			interest_management_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * InterestManagementCandidate
 * @extends HusleieAPI2
 */
export class InterestManagementCandidateApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "InterestManagementCandidate"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {} comment_id Required InterestManagement Candidate Comment ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordInterestManagementCandidateComment}
	 * @returns {Promise} A promise
	 */
	deleteLandlordInterestManagementCandidateComment(landlord_id, interest_management_id, candidate_id, comment_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordInterestManagementCandidateComment" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/comments/{comment_id}', {
			landlord_id,
			interest_management_id,
			candidate_id,
			comment_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {} reference_id Required InterestManagement Candidate Reference ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordInterestManagementCandidateReference}
	 * @returns {Promise} A promise
	 */
	deleteLandlordInterestManagementCandidateReference(landlord_id, interest_management_id, candidate_id, reference_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordInterestManagementCandidateReference" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/references/{reference_id}', {
			landlord_id,
			interest_management_id,
			candidate_id,
			reference_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deletetLandlordInterestManagementCandidate}
	 * @returns {Promise} A promise
	 */
	deletetLandlordInterestManagementCandidate(landlord_id, interest_management_id, candidate_id, responseType = 'json') {

		this.logger('Calling API function "deletetLandlordInterestManagementCandidate" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}', {
			landlord_id,
			interest_management_id,
			candidate_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordInterestManagementCandidate}
	 * @returns {Promise} A promise
	 */
	getLandlordInterestManagementCandidate(landlord_id, interest_management_id, candidate_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordInterestManagementCandidate" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}', {
			landlord_id,
			interest_management_id,
			candidate_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.application 
	 * @param {string} data.body.application.introduction Introducationary text about the applicant
	 * @param {integer} data.body.application.numberOfTenants Number of people living together Default: [0]
	 * @param {string} data.body.application.tenantProof Tenant Proofing reference
	 * @param {string} data.body.email email address for the candidate
	 * @param {string} data.body.name Name of the candidate
	 * @param {string} data.body.phone Phone number for the candidate
	 * @param {integer} data.body.rank The rank of the candidate

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchLandlordInterestManagementCandidate}
	 * @returns {Promise} A promise
	 */
	patchLandlordInterestManagementCandidate(landlord_id, interest_management_id, candidate_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchLandlordInterestManagementCandidate" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"application":{"properties":{"introduction":{"description":"Introducationary text about the applicant","nullable":false,"type":"string"},"numberOfTenants":{"default":"0","description":"Number of people living together","nullable":false,"type":"integer"},"tenantProof":{"description":"Tenant Proofing reference","nullable":true,"pattern":"^\\d{8}\\/\\d\\/\\w{6}$","type":"string"}},"type":"object"},"email":{"description":"email address for the candidate","format":"email","nullable":false,"type":"string"},"name":{"description":"Name of the candidate","nullable":false,"type":"string"},"phone":{"description":"Phone number for the candidate","nullable":true,"type":"string"},"rank":{"description":"The rank of the candidate","maximum":5,"minimum":1,"nullable":true,"type":"integer"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}', {
			landlord_id,
			interest_management_id,
			candidate_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {} comment_id Required InterestManagement Candidate Comment ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.author Name of the commentor
	 * @param {integer} data.body.author_id ID of the commentor
	 * @param {string} data.body.comment The main body of the comment
	 * @param {object} data.body.date 
	 * @param {string} data.body.date.created The UTC date and time of when comment were created
	 * @param {string} data.body.date.edited The UTC date and time of when comment were updated
	 * @param {string} data.body.id UUID of the comment

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchLandlordInterestManagementCandidateComment}
	 * @returns {Promise} A promise
	 */
	patchLandlordInterestManagementCandidateComment(landlord_id, interest_management_id, candidate_id, comment_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchLandlordInterestManagementCandidateComment" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"author":{"description":"Name of the commentor","nullable":false,"readOnly":true,"type":"string"},"author_id":{"description":"ID of the commentor","nullable":false,"readOnly":true,"type":"integer"},"comment":{"description":"The main body of the comment","nullable":false,"type":"string"},"date":{"properties":{"created":{"description":"The UTC date and time of when comment were created","format":"date-time","nullable":false,"type":"string"},"edited":{"description":"The UTC date and time of when comment were updated","format":"date-time","nullable":true,"type":"string"}},"readOnly":true,"type":"object"},"id":{"description":"UUID of the comment","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","readOnly":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/comments/{comment_id}', {
			landlord_id,
			interest_management_id,
			candidate_id,
			comment_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {} reference_id Required InterestManagement Candidate Reference ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.category Who is the reference from Enum: ['employer', 'landlord', 'family'] Default: [landlord]
	 * @param {string} data.body.comment If landlord has a comment to the reference check
	 * @param {object} data.body.date 
	 * @param {string} data.body.date.end Date (not ISO, but string) of when the relation ended
	 * @param {string} data.body.date.start Date (not ISO, but string) of when the relation started
	 * @param {string} data.body.email email address for the reference
	 * @param {string} data.body.id UUID of the comment
	 * @param {string} data.body.name Name of the reference
	 * @param {string} data.body.phone Phone number for the reference
	 * @param {boolean} data.body.rating Boolean if reference check is good or bad

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchLandlordInterestManagementCandidateReference}
	 * @returns {Promise} A promise
	 */
	patchLandlordInterestManagementCandidateReference(landlord_id, interest_management_id, candidate_id, reference_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchLandlordInterestManagementCandidateReference" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"category":{"default":"landlord","description":"Who is the reference from","enum":["employer","landlord","family"],"type":"string"},"comment":{"description":"If landlord has a comment to the reference check","nullable":true,"type":"string"},"date":{"properties":{"end":{"description":"Date (not ISO, but string) of when the relation ended","nullable":true,"type":"string"},"start":{"description":"Date (not ISO, but string) of when the relation started","nullable":true,"type":"string"}},"readOnly":true,"type":"object"},"email":{"description":"email address for the reference","format":"email","nullable":true,"type":"string"},"id":{"description":"UUID of the comment","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","readOnly":true,"type":"string"},"name":{"description":"Name of the reference","nullable":false,"type":"string"},"phone":{"description":"Phone number for the reference","nullable":true,"type":"string"},"rating":{"description":"Boolean if reference check is good or bad","nullable":true,"type":"boolean"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/references/{reference_id}', {
			landlord_id,
			interest_management_id,
			candidate_id,
			reference_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.author Name of the commentor
	 * @param {integer} data.body.author_id ID of the commentor
	 * @param {string} data.body.comment The main body of the comment
	 * @param {object} data.body.date 
	 * @param {string} data.body.date.created The UTC date and time of when comment were created
	 * @param {string} data.body.date.edited The UTC date and time of when comment were updated
	 * @param {string} data.body.id UUID of the comment

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordInterestManagementCandidateComment}
	 * @returns {Promise} A promise
	 */
	postLandlordInterestManagementCandidateComment(landlord_id, interest_management_id, candidate_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordInterestManagementCandidateComment" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"author":{"description":"Name of the commentor","nullable":false,"readOnly":true,"type":"string"},"author_id":{"description":"ID of the commentor","nullable":false,"readOnly":true,"type":"integer"},"comment":{"description":"The main body of the comment","nullable":false,"type":"string"},"date":{"properties":{"created":{"description":"The UTC date and time of when comment were created","format":"date-time","nullable":false,"type":"string"},"edited":{"description":"The UTC date and time of when comment were updated","format":"date-time","nullable":true,"type":"string"}},"readOnly":true,"type":"object"},"id":{"description":"UUID of the comment","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","readOnly":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/comments', {
			landlord_id,
			interest_management_id,
			candidate_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {} invite_to Required InterestManagement Candidate Status
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.date 
	 * @param {string} data.body.date.end When the viewing ends
	 * @param {string} data.body.date.start When the viewing starts
	 * @param {string} data.body.inviteTo The other fields anly used when this is viewing Enum: ['credit_check', 'viewing']
	 * @param {string} data.body.message If a message should be attached to the viewing invite

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordInterestManagementCandidateInvite}
	 * @returns {Promise} A promise
	 */
	postLandlordInterestManagementCandidateInvite(landlord_id, interest_management_id, candidate_id, invite_to, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordInterestManagementCandidateInvite" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"date":{"properties":{"end":{"description":"When the viewing ends","format":"date-time","nullable":false,"type":"string"},"start":{"description":"When the viewing starts","format":"date-time","nullable":false,"type":"string"}},"type":"object"},"inviteTo":{"description":"The other fields anly used when this is viewing","enum":["credit_check","viewing"],"type":"string"},"message":{"description":"If a message should be attached to the viewing invite","nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/invite/{invite_to}', {
			landlord_id,
			interest_management_id,
			candidate_id,
			invite_to,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.message Name of the candidate

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordInterestManagementCandidateMessage}
	 * @returns {Promise} A promise
	 */
	postLandlordInterestManagementCandidateMessage(landlord_id, interest_management_id, candidate_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordInterestManagementCandidateMessage" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"message":{"description":"Name of the candidate","nullable":false,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/messages', {
			landlord_id,
			interest_management_id,
			candidate_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.message_id Message ID to mark as read

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordInterestManagementCandidateMessageMarkAsRead}
	 * @returns {Promise} A promise
	 */
	postLandlordInterestManagementCandidateMessageMarkAsRead(landlord_id, interest_management_id, candidate_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordInterestManagementCandidateMessageMarkAsRead" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"message_id":{"description":"Message ID to mark as read","nullable":true,"type":"integer"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/messages/mark_as_read', {
			landlord_id,
			interest_management_id,
			candidate_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.category Who is the reference from Enum: ['employer', 'landlord', 'family'] Default: [landlord]
	 * @param {string} data.body.comment If landlord has a comment to the reference check
	 * @param {object} data.body.date 
	 * @param {string} data.body.date.end Date (not ISO, but string) of when the relation ended
	 * @param {string} data.body.date.start Date (not ISO, but string) of when the relation started
	 * @param {string} data.body.email email address for the reference
	 * @param {string} data.body.id UUID of the comment
	 * @param {string} data.body.name Name of the reference
	 * @param {string} data.body.phone Phone number for the reference
	 * @param {boolean} data.body.rating Boolean if reference check is good or bad

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordInterestManagementCandidateReference}
	 * @returns {Promise} A promise
	 */
	postLandlordInterestManagementCandidateReference(landlord_id, interest_management_id, candidate_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordInterestManagementCandidateReference" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"category":{"default":"landlord","description":"Who is the reference from","enum":["employer","landlord","family"],"type":"string"},"comment":{"description":"If landlord has a comment to the reference check","nullable":true,"type":"string"},"date":{"properties":{"end":{"description":"Date (not ISO, but string) of when the relation ended","nullable":true,"type":"string"},"start":{"description":"Date (not ISO, but string) of when the relation started","nullable":true,"type":"string"}},"readOnly":true,"type":"object"},"email":{"description":"email address for the reference","format":"email","nullable":true,"type":"string"},"id":{"description":"UUID of the comment","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","readOnly":true,"type":"string"},"name":{"description":"Name of the reference","nullable":false,"type":"string"},"phone":{"description":"Phone number for the reference","nullable":true,"type":"string"},"rating":{"description":"Boolean if reference check is good or bad","nullable":true,"type":"boolean"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/references', {
			landlord_id,
			interest_management_id,
			candidate_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} interest_management_id Required Interest Management Project ID
	 * @param {} candidate_id Required InterestManagement Candidate ID
	 * @param {} candidate_status Required InterestManagement Candidate Status
	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordInterestManagementCandidateStatus}
	 * @returns {Promise} A promise
	 */
	postLandlordInterestManagementCandidateStatus(landlord_id, interest_management_id, candidate_id, candidate_status, responseType = 'json') {

		this.logger('Calling API function "postLandlordInterestManagementCandidateStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/interest_managements/{interest_management_id}/candidates/{candidate_id}/status/{candidate_status}', {
			landlord_id,
			interest_management_id,
			candidate_id,
			candidate_status,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Invoice
 * @extends HusleieAPI2
 */
export class InvoiceApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Invoice"...');

	}


	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {array} data.body.distribution_rules 
	 * @param {string} data.body.distribution_rules[].delayUntil 
	 * @param {string} data.body.distribution_rules[].method  Enum: ['dummy', 'sms', 'email', 'ehf', 'efaktura', 'vipps']
	 * @param {string} data.body.distribution_rules[].onFail  Enum: ['continue', 'skip-next', 'stop', 'fail']
	 * @param {string} data.body.distribution_rules[].onSuccess  Enum: ['continue', 'skip-next', 'stop', 'fail']
	 * @param {integer} data.body.invoice_id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createDistribution}
	 * @returns {Promise} A promise
	 */
	createDistribution({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createDistribution" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"distribution_rules":{"items":{"properties":{"delayUntil":{"format":"date-time","type":"string"},"method":{"enum":["dummy","sms","email","ehf","efaktura","vipps"],"type":"string"},"onFail":{"enum":["continue","skip-next","stop","fail"],"type":"string"},"onSuccess":{"enum":["continue","skip-next","stop","fail"],"type":"string"}},"required":["method","onFail","onSuccess"],"type":"object"},"minItems":1,"type":"array"},"invoice_id":{"type":"integer"}},"required":["invoice_id","distribution_rules"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/invoice/distribution';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteDistribution}
	 * @returns {Promise} A promise
	 */
	deleteDistribution(id, responseType = 'json') {

		this.logger('Calling API function "deleteDistribution" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/invoice/distribution/{id}', {
			id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getDistribution}
	 * @returns {Promise} A promise
	 */
	getDistribution(id, responseType = 'json') {

		this.logger('Calling API function "getDistribution" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/invoice/distribution/{id}', {
			id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.status {}
	 * @param data.query.page {}
	 * @param data.query.rows {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/getDistributionList}
	 * @returns {Promise} A promise
	 */
	getDistributionList({ query } = {}, responseType = 'json') {

		this.logger('Calling API function "getDistributionList" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/invoice/distribution';

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.level {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/getDistributionLog}
	 * @returns {Promise} A promise
	 */
	getDistributionLog({ query } = {}, responseType = 'json') {

		this.logger('Calling API function "getDistributionLog" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/invoice/distribution/{id}/log';

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Landlord
 * @extends HusleieAPI2
 */
export class LandlordApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Landlord"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordAvailableSubscriptions}
	 * @returns {Promise} A promise
	 */
	getLandlordAvailableSubscriptions(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordAvailableSubscriptions" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/available_subscriptions', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.refresh {}Trigger a refresh, not available in production
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordDashboard}
	 * @returns {Promise} A promise
	 */
	getLandlordDashboard(landlord_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordDashboard" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/dashboard', {
			landlord_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Return the identity of the landlord profile
	 * @param {} landlord_id Required Landlord ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordIdentity}
	 * @returns {Promise} A promise
	 */
	getLandlordIdentity(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordIdentity" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/identity', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.query {}Only list elements that contains specified string
	 * @param data.query.limit {}Limit amount of items returned, if 0 everything will be returned
	 * @param data.query.page {}Which page to display, only respected if limit has been specified
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordSearch}
	 * @returns {Promise} A promise
	 */
	getLandlordSearch(landlord_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordSearch" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/search', {
			landlord_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} year Year to get report for
	 * @param {} statement_id Statement ID to get report for
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordYearlyReport}
	 * @returns {Promise} A promise
	 */
	getLandlordYearlyReport(landlord_id, year, statement_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordYearlyReport" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/yearly_report/{year}/{statement_id}', {
			landlord_id,
			year,
			statement_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Set the identity of the landlord profile using a identification session id, a combination of name and nin, or a organization number
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.identificationSessionId 
	 * @param {string} data.body.name 
	 * @param {string} data.body.nationalIdentityNumber 

	 * @see {@link file:///./redoc-husleieapi.html#operation/setLandlordIdentity}
	 * @returns {Promise} A promise
	 */
	setLandlordIdentity(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "setLandlordIdentity" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"identificationSessionId":{"type":"string"},"name":{"type":"string"},"nationalIdentityNumber":{"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/identity', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * LandlordAccounting
 * @extends HusleieAPI2
 */
export class LandlordAccountingApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "LandlordAccounting"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.sortBy {}Which column should we sort by
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordAccountingDueRent}
	 * @returns {Promise} A promise
	 */
	getLandlordAccountingDueRent(landlord_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordAccountingDueRent" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/accounting/due_rent', {
			landlord_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * LandlordReport
 * @extends HusleieAPI2
 */
export class LandlordReportApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "LandlordReport"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} report Which report to retreive
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.format {}Which format to return data in
	 * @param data.query.asOfPeriod {}From which period should we as of include, only valid for disbursed
	 * @param data.query.evenPeriod {}To which period should we even include
	 * @param data.query.evenDate {}To which date should we even include
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordAccountingReportsInvoices}
	 * @returns {Promise} A promise
	 */
	getLandlordAccountingReportsInvoices(landlord_id, report, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordAccountingReportsInvoices" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/accounting/reports/invoices/{report}', {
			landlord_id,
			report,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.asOfPeriod {}From which period should we as of include
	 * @param data.query.evenPeriod {}To which period should we even include
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordAccountingReportsInvoicesVouchersDownload}
	 * @returns {Promise} A promise
	 */
	getLandlordAccountingReportsInvoicesVouchersDownload(landlord_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordAccountingReportsInvoicesVouchersDownload" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/accounting/reports/invoices/vouchers/download', {
			landlord_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} report Which report to retreive
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.format {}Which format to return data in
	 * @param data.query.asOfPeriod {}From which period should we as of include, only valid for disbursed
	 * @param data.query.evenPeriod {}To which period should we even include
	 * @param data.query.evenDate {}To which date should we even include
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordAccountingReportsPayments}
	 * @returns {Promise} A promise
	 */
	getLandlordAccountingReportsPayments(landlord_id, report, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordAccountingReportsPayments" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/accounting/reports/payments/{report}', {
			landlord_id,
			report,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} report Which report to retreive
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.format {}Which format to return data in
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordReports}
	 * @returns {Promise} A promise
	 */
	getLandlordReports(landlord_id, report, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordReports" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/reports/{report}', {
			landlord_id,
			report,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordReportsSettingsRentalObjects}
	 * @returns {Promise} A promise
	 */
	getLandlordReportsSettingsRentalObjects(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordReportsSettingsRentalObjects" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/reports/settings/rental_objects', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordReportsSettingsTenancies}
	 * @returns {Promise} A promise
	 */
	getLandlordReportsSettingsTenancies(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordReportsSettingsTenancies" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/reports/settings/tenancies', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {array} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordReportsSettingsRentalObjects}
	 * @returns {Promise} A promise
	 */
	postLandlordReportsSettingsRentalObjects(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordReportsSettingsRentalObjects" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"items":{"properties":{"customClientId":{"nullable":true,"type":"string"},"landlordId":{"nullable":false,"type":"number"},"rentalObjectAlias":{"nullable":true,"type":"string"},"rentalObjectApartmentNumber":{"nullable":true,"type":"string"},"rentalObjectId":{"nullable":false,"type":"number"},"rentalObjectRoomNumber":{"nullable":true,"type":"string"},"rentalObjectStreet":{"nullable":false,"type":"string"}},"type":"object"},"type":"array"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/reports/settings/rental_objects', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {array} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordReportsSettingsTenancies}
	 * @returns {Promise} A promise
	 */
	postLandlordReportsSettingsTenancies(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordReportsSettingsTenancies" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"items":{"properties":{"customClientId":{"nullable":true,"type":"string"},"landlordId":{"nullable":false,"type":"number"},"rentalObjectStreet":{"nullable":false,"type":"string"},"tenancyId":{"nullable":false,"type":"number"},"tenancyStatus":{"nullable":false,"type":"string"},"tenantName":{"nullable":false,"type":"string"}},"type":"object"},"type":"array"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/reports/settings/tenancies', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * LeaseTermination
 * @extends HusleieAPI2
 */
export class LeaseTerminationApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "LeaseTermination"...');

	}


	/**
	 * Delete a lease termination session
	 * @param {} lease_termination_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLeaseTerminationSession}
	 * @returns {Promise} A promise
	 */
	deleteLeaseTerminationSession(lease_termination_id, responseType = 'json') {

		this.logger('Calling API function "deleteLeaseTerminationSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/lease_termination/{lease_termination_id}', {
			lease_termination_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Letting
 * @extends HusleieAPI2
 */
export class LettingApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Letting"...');

	}


	/**
	 * Creates a new letting
	 * @param {} landlord_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.rentalObjectId 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createLetting}
	 * @returns {Promise} A promise
	 */
	createLetting(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createLetting" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"rentalObjectId":{"type":"integer"}},"required":["rentalObjectId"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/lettings/', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Marks a letting as deleted
	 * @param {} landlord_id
	 * @param {} letting_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLetting}
	 * @returns {Promise} A promise
	 */
	deleteLetting(landlord_id, letting_id, responseType = 'json') {

		this.logger('Calling API function "deleteLetting" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/lettings/{letting_id}', {
			landlord_id,
			letting_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Fetch a specific letting, including related status information.
	 * @param {} landlord_id
	 * @param {} letting_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLetting}
	 * @returns {Promise} A promise
	 */
	getLetting(landlord_id, letting_id, responseType = 'json') {

		this.logger('Calling API function "getLetting" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/lettings/{letting_id}', {
			landlord_id,
			letting_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns a list of lettings belonging to the calling Landlord entity. Will not return lettings marked as deleted.
	 * @param {} landlord_id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.sortBy {}Which column should we sort by
	 * @param data.query.sortByOrder {}Which order column should we sort by
	 * @param data.query.limit {}Limit amount of items returned, if 0 everything will be returned
	 * @param data.query.page {}Which page to display, only respected if limit has been specified
	 * @param data.query.status {}
	 * @param data.query.rentalObjectId {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLettings}
	 * @returns {Promise} A promise
	 */
	getLettings(landlord_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLettings" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/lettings/', {
			landlord_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Publish a letting to a specific publisher, or republish if the letting is already published.
	 * @param {} landlord_id
	 * @param {} letting_id Letting id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.identificationId 
	 * @param {} data.body.publishers 

	 * @see {@link file:///./redoc-husleieapi.html#operation/publishLetting}
	 * @returns {Promise} A promise
	 */
	publishLetting(landlord_id, letting_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "publishLetting" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"identificationId":{"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"publishers":{"oneOf":[{"items":{"properties":{"name":{"type":"string"},"product":{"type":"string"}},"required":["name"],"type":"object"},"type":"array"},{"properties":{"name":{"type":"string"},"product":{"type":"string"}},"required":["name"],"type":"object"}]}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/lettings/{letting_id}/publish', {
			landlord_id,
			letting_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Unpublish a letting
	 * @param {} landlord_id
	 * @param {} letting_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.name 

	 * @see {@link file:///./redoc-husleieapi.html#operation/unpublishLetting}
	 * @returns {Promise} A promise
	 */
	unpublishLetting(landlord_id, letting_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "unpublishLetting" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"name":{"type":"string"}},"required":["name"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/lettings/{letting_id}/unpublish', {
			landlord_id,
			letting_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Update the full letting object
	 * @param {} landlord_id
	 * @param {} letting_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.broker 
	 * @param {string} data.body.broker.cellPhone 
	 * @param {string} data.body.broker.email 
	 * @param {string} data.body.broker.name 
	 * @param {string} data.body.broker.title  Default: [Utleier]
	 * @param {array} data.body.content 
	 * @param {string} data.body.content[].sectionContent 
	 * @param {string} data.body.content[].sectionHeading 
	 * @param {object} data.body.displayOptions 
	 * @param {boolean} data.body.displayOptions.contract  Default: [0]
	 * @param {boolean} data.body.displayOptions.creditCheck  Default: [0]
	 * @param {boolean} data.body.displayOptions.depAccount  Default: [0]
	 * @param {boolean} data.body.displayOptions.depGuarantee  Default: [0]
	 * @param {boolean} data.body.displayOptions.rentAdm  Default: [0]
	 * @param {boolean} data.body.displayOptions.tenantProof  Default: [0]
	 * @param {array} data.body.media 
	 * @param {string} data.body.media[].description 
	 * @param {string} data.body.media[].display  Enum: ['none', 'ref', 'carousel'] Default: [carousel]
	 * @param {array} data.body.media[].mediaLinks 
	 * @param {string} data.body.media[].mediaLinks[].contentType 
	 * @param {string} data.body.media[].mediaLinks[].type  Enum: ['original', 'thumbnail']
	 * @param {string} data.body.media[].mediaLinks[].url 
	 * @param {integer} data.body.media[].mediaLinks[].width 
	 * @param {string} data.body.media[].token 
	 * @param {string} data.body.media[].type  Enum: ['image', 'video', 'pdf'] Default: [image]
	 * @param {object} data.body.rent 
	 * @param {string} data.body.rent.paymentPeriod  Enum: ['monthly'] Default: [monthly]
	 * @param {string} data.body.rent.rentIncludes 
	 * @param {object} data.body.rent.rentPeriod 
	 * @param {string} data.body.rent.rentPeriod.from 
	 * @param {string} data.body.rent.rentPeriod.to 
	 * @param {object} data.body.rent.rentPrice 
	 * @param {number} data.body.rent.rentPrice.amount 
	 * @param {string} data.body.rent.rentPrice.currency  Default: [NOK]
	 * @param {object} data.body.rent.rentSecurity 
	 * @param {number} data.body.rent.rentSecurity.amount 
	 * @param {string} data.body.rent.rentSecurity.description 
	 * @param {object} data.body.rentalObject 
	 * @param {object} data.body.rentalObject.address 
	 * @param {string} data.body.rentalObject.address.postalArea 
	 * @param {string} data.body.rentalObject.address.postalZipCode 
	 * @param {string} data.body.rentalObject.address.street 
	 * @param {object} data.body.rentalObject.area 
	 * @param {string} data.body.rentalObject.area.primaryRoom 
	 * @param {integer} data.body.rentalObject.bedrooms 
	 * @param {string} data.body.rentalObject.constructionYear 
	 * @param {object} data.body.rentalObject.ems 
	 * @param {string} data.body.rentalObject.ems.energyLabel  Enum: ['A', 'B', 'C', 'D', 'E', 'F', 'G']
	 * @param {string} data.body.rentalObject.ems.heatingLabel  Enum: ['RED', 'ORANGE', 'YELLOW', 'LIGHT_GREEN', 'DARK_GREEN']
	 * @param {number} data.body.rentalObject.floor 
	 * @param {object} data.body.rentalObject.geoLocation 
	 * @param {number} data.body.rentalObject.geoLocation.lat 
	 * @param {number} data.body.rentalObject.geoLocation.lon 
	 * @param {integer} data.body.rentalObject.matrikkelId 
	 * @param {number} data.body.rentalObject.parkingSpots 
	 * @param {array} data.body.rentalObject.preferences 
	 * @param {string} data.body.rentalObject.preferences[].name  Enum: ['ACCESSIBILITY_LEVEL', 'AIRCONDITIONING', 'ALARM', 'BALCONY', 'BROADBAND', 'CABLE-TV', 'CENTRAL', 'CHILD-FRIENDLY', 'CHARGING', 'COMMONWASHROOM', 'FIREPLACE', 'FURNISHED', 'GARDEN', 'GARAGE', 'HIKING', 'JANITORSERVICE', 'LIFT', 'MODERN', 'NO-NEIGHBOURS-OP', 'PARQUETT', 'PARTLY_FURNISHED', 'PETSALLOWED', 'QUIET-AREA', 'UNFURNISHED', 'VIEW']
	 * @param {string} data.body.rentalObject.propertyType  Enum: ['apartment', 'house', 'dorm', 'co-op', 'basement flat', 'semi-detached', 'chained-house', 'commercial', 'garage-parking']
	 * @param {string} data.body.rentalObject.renovatedYear 
	 * @param {array} data.body.viewings 
	 * @param {string} data.body.viewings[].comment 
	 * @param {string} data.body.viewings[].contact 
	 * @param {string} data.body.viewings[].from 
	 * @param {string} data.body.viewings[].to 
	 * @param {string} data.body.viewings[].type 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLetting}
	 * @returns {Promise} A promise
	 */
	updateLetting(landlord_id, letting_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLetting" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"broker":{"description":"","properties":{"cellPhone":{"type":"string"},"email":{"format":"email","type":"string"},"name":{"type":"string"},"title":{"default":"Utleier","type":"string"}},"title":"LettingBroker","type":"object"},"content":{"items":{"properties":{"sectionContent":{"type":"string"},"sectionHeading":{"type":"string"}},"title":"LettingContent","type":"object"},"type":"array"},"displayOptions":{"properties":{"contract":{"default":false,"type":"boolean"},"creditCheck":{"default":false,"type":"boolean"},"depAccount":{"default":false,"type":"boolean"},"depGuarantee":{"default":false,"type":"boolean"},"rentAdm":{"default":false,"type":"boolean"},"tenantProof":{"default":false,"type":"boolean"}},"title":"LettingDisplayOptions","type":"object"},"media":{"items":{"properties":{"description":{"type":"string"},"display":{"default":"carousel","enum":["none","ref","carousel"],"type":"string"},"mediaLinks":{"items":{"properties":{"contentType":{"type":"string"},"type":{"enum":["original","thumbnail"],"type":"string"},"url":{"type":"string"},"width":{"type":"integer"}},"required":["type","contentType","url"],"type":"object"},"readOnly":true,"title":"LettingMediaLinks","type":"array"},"token":{"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"type":{"default":"image","enum":["image","video","pdf"],"type":"string"}},"title":"LettingMedia","type":"object"},"type":"array"},"rent":{"properties":{"paymentPeriod":{"default":"monthly","enum":["monthly"],"type":"string"},"rentIncludes":{"type":"string"},"rentPeriod":{"properties":{"from":{"format":"date","type":"string"},"to":{"format":"date","type":"string"}},"type":"object"},"rentPrice":{"properties":{"amount":{"type":"number"},"currency":{"default":"NOK","maxLength":3,"minLength":3,"pattern":"^[A-Z]+$","type":"string"}},"type":"object"},"rentSecurity":{"properties":{"amount":{"type":"number"},"description":{"type":"string"}},"type":"object"}},"title":"LettingRent","type":"object"},"rentalObject":{"properties":{"address":{"properties":{"postalArea":{"type":"string"},"postalZipCode":{"pattern":"^\\d{4}$","type":"string"},"street":{"type":"string"}},"type":"object"},"area":{"properties":{"primaryRoom":{"type":"string"}},"type":"object"},"bedrooms":{"type":"integer"},"constructionYear":{"nullable":true,"pattern":"^\\d{4}$","type":"string"},"ems":{"properties":{"energyLabel":{"enum":["A","B","C","D","E","F","G"],"type":"string"},"heatingLabel":{"enum":["RED","ORANGE","YELLOW","LIGHT_GREEN","DARK_GREEN"],"type":"string"}},"type":"object"},"floor":{"nullable":true,"type":"number"},"geoLocation":{"properties":{"lat":{"type":"number"},"lon":{"type":"number"}},"type":"object"},"matrikkelId":{"type":"integer"},"parkingSpots":{"type":"number"},"preferences":{"items":{"properties":{"name":{"enum":["ACCESSIBILITY_LEVEL","AIRCONDITIONING","ALARM","BALCONY","BROADBAND","CABLE-TV","CENTRAL","CHILD-FRIENDLY","CHARGING","COMMONWASHROOM","FIREPLACE","FURNISHED","GARDEN","GARAGE","HIKING","JANITORSERVICE","LIFT","MODERN","NO-NEIGHBOURS-OP","PARQUETT","PARTLY_FURNISHED","PETSALLOWED","QUIET-AREA","UNFURNISHED","VIEW"],"type":"string"}},"type":"object"},"type":"array"},"propertyType":{"enum":["apartment","house","dorm","co-op","basement flat","semi-detached","chained-house","commercial","garage-parking"],"type":"string"},"renovatedYear":{"pattern":"^\\d{4}$","type":"string"}},"title":"LettingObject","type":"object"},"viewings":{"items":{"properties":{"comment":{"type":"string"},"contact":{"type":"string"},"from":{"format":"date-time","type":"string"},"to":{"format":"date-time","type":"string"},"type":{"type":"string"}},"title":"LettingViewing","type":"object"},"type":"array"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/lettings/{letting_id}', {
			landlord_id,
			letting_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Payment
 * @extends HusleieAPI2
 */
export class PaymentApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Payment"...');

	}


	/**
	 * Returns the stripe public and setup intent secret
	 * @see {@link file:///./redoc-husleieapi.html#operation/CreateSetupIntent}
	 * @returns {Promise} A promise
	 */
	CreateSetupIntent(responseType = 'json') {

		this.logger('Calling API function "CreateSetupIntent" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/payment/create_setup_intent';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Product
 * @extends HusleieAPI2
 */
export class ProductApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Product"...');

	}


	/**
	 * Creates a new datastructure to enable a tenant to create a new deposit account with our partner.
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 
	 * @param {object} data.body.deposit 
	 * @param {integer} data.body.deposit.amount 
	 * @param {integer} data.body.deposit.months 
	 * @param {boolean} data.body.deposit.ownDefined 
	 * @param {object} data.body.landlord 
	 * @param {object} data.body.landlord.company 
	 * @param {string} data.body.landlord.company.name 
	 * @param {string} data.body.landlord.company.orgNumber 
	 * @param {string} data.body.landlord.email 
	 * @param {string} data.body.landlord.name 
	 * @param {string} data.body.landlord.phone 
	 * @param {string} data.body.landlord.ssn 
	 * @param {object} data.body.rent 
	 * @param {string} data.body.rent.accountNumber 
	 * @param {string} data.body.rent.durationType 
	 * @param {string} data.body.rent.endDate 
	 * @param {string} data.body.rent.noticePeriod 
	 * @param {string} data.body.rent.ownNoticePeriod 
	 * @param {string} data.body.rent.payDay 
	 * @param {integer} data.body.rent.rent 
	 * @param {string} data.body.rent.startDate 
	 * @param {integer} data.body.rental_object_id 
	 * @param {object} data.body.tenant 
	 * @param {string} data.body.tenant.email 
	 * @param {string} data.body.tenant.name 
	 * @param {string} data.body.tenant.phone 
	 * @param {object} data.body.upsale 
	 * @param {object} data.body.upsale.rentadm 
	 * @param {string} data.body.upsale.rentadm.plan  Enum: ['none', 'basic', 'premium']

	 * @see {@link file:///./redoc-husleieapi.html#operation/create_deposit_account}
	 * @returns {Promise} A promise
	 */
	create_deposit_account({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "create_deposit_account" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"deposit":{"properties":{"amount":{"type":"integer"},"months":{"type":"integer"},"ownDefined":{"type":"boolean"}},"required":["months","ownDefined","amount"],"type":"object"},"landlord":{"properties":{"company":{"properties":{"name":{"type":"string"},"orgNumber":{"type":"string"}},"type":"object"},"email":{"type":"string"},"name":{"type":"string"},"phone":{"type":"string"},"ssn":{"nullable":true,"type":"string"}},"required":["name","type"],"type":"object"},"rent":{"properties":{"accountNumber":{"minLength":11,"type":"string"},"durationType":{"type":"string"},"endDate":{"nullable":true,"type":"string"},"noticePeriod":{"type":"string"},"ownNoticePeriod":{"type":"string"},"payDay":{"type":"string"},"rent":{"type":"integer"},"startDate":{"type":"string"}},"required":["durationType","startDate","rent"],"type":"object"},"rental_object_id":{"type":"integer"},"tenant":{"properties":{"email":{"type":"string"},"name":{"type":"string"},"phone":{"type":"string"}},"required":["name","email","phone"],"type":"object"},"upsale":{"properties":{"rentadm":{"properties":{"plan":{"enum":["none","basic","premium"],"type":"string"}},"type":"object"}},"type":"object"}},"required":["rental_object_id","landlord","tenant","rent","deposit"]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/product/deposit_account';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getDepositAccount}
	 * @returns {Promise} A promise
	 */
	getDepositAccount(id, responseType = 'json') {

		this.logger('Calling API function "getDepositAccount" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/product/deposit_account/{id}', {
			id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getDepositAccountSigningUrl}
	 * @returns {Promise} A promise
	 */
	getDepositAccountSigningUrl(id, responseType = 'json') {

		this.logger('Calling API function "getDepositAccountSigningUrl" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/product/deposit_account/{id}/signing_url', {
			id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSecurityDeposit}
	 * @returns {Promise} A promise
	 */
	getSecurityDeposit(id, responseType = 'json') {

		this.logger('Calling API function "getSecurityDeposit" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/product/security_deposit/{id}', {
			id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.role {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSecurityDepositSigningUrl}
	 * @returns {Promise} A promise
	 */
	getSecurityDepositSigningUrl(id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getSecurityDepositSigningUrl" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/product/security_deposit/{id}/signing_url', {
			id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {boolean} data.body.consentEComm  Default: [0]
	 * @param {boolean} data.body.consentMarketing  Default: [0]
	 * @param {boolean} data.body.consentTerms  Default: [0]
	 * @param {string} data.body.email 
	 * @param {string} data.body.firstName 
	 * @param {array} data.body.fundsOrigin 
	 * @param {string} data.body.fundsOriginOther 
	 * @param {boolean} data.body.isForeignCitizen  Default: [0]
	 * @param {boolean} data.body.isPep  Default: [0]
	 * @param {string} data.body.lastName 
	 * @param {string} data.body.nationality 
	 * @param {string} data.body.otherTaxingCountry 
	 * @param {string} data.body.pepDescription 
	 * @param {string} data.body.phone 
	 * @param {string} data.body.ssn 
	 * @param {string} data.body.taxIdentityNumber 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postDepositAccountKCYForm}
	 * @returns {Promise} A promise
	 */
	postDepositAccountKCYForm(id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postDepositAccountKCYForm" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"consentEComm":{"default":false,"nullable":true,"type":"boolean"},"consentMarketing":{"default":false,"type":"boolean"},"consentTerms":{"default":false,"type":"boolean"},"email":{"type":"string"},"firstName":{"type":"string"},"fundsOrigin":{"items":{"type":"string"},"type":"array"},"fundsOriginOther":{"type":"string"},"isForeignCitizen":{"default":false,"type":"boolean"},"isPep":{"default":false,"type":"boolean"},"lastName":{"type":"string"},"nationality":{"maxLength":3,"pattern":"^[A-Z]+$","type":"string"},"otherTaxingCountry":{"nullable":true,"type":"string"},"pepDescription":{"nullable":true,"type":"string"},"phone":{"nullable":true,"pattern":"^[0-9-+ ]+$","type":"string"},"ssn":{"maxLength":11,"nullable":true,"pattern":"^[0-9]{11}$","type":"string"},"taxIdentityNumber":{"nullable":true,"type":"string"}},"required":["firstName","lastName","ssn","phone","email","nationality","fundsOrigin","isPep","isForeignCitizen","consentTerms","consentMarketing"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/product/deposit_account/{id}/kyc', {
			id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {boolean} data.body.consentEComm  Default: [0]
	 * @param {boolean} data.body.consentMarketing  Default: [0]
	 * @param {boolean} data.body.consentTerms  Default: [0]
	 * @param {string} data.body.email 
	 * @param {string} data.body.firstName 
	 * @param {array} data.body.fundsOrigin 
	 * @param {string} data.body.fundsOriginOther 
	 * @param {boolean} data.body.isForeignCitizen  Default: [0]
	 * @param {boolean} data.body.isPep  Default: [0]
	 * @param {string} data.body.lastName 
	 * @param {string} data.body.nationality 
	 * @param {string} data.body.otherTaxingCountry 
	 * @param {string} data.body.pepDescription 
	 * @param {string} data.body.phone 
	 * @param {string} data.body.ssn 
	 * @param {string} data.body.taxIdentityNumber 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postSecurityDepositKCYForm}
	 * @returns {Promise} A promise
	 */
	postSecurityDepositKCYForm(id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postSecurityDepositKCYForm" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"consentEComm":{"default":false,"nullable":true,"type":"boolean"},"consentMarketing":{"default":false,"type":"boolean"},"consentTerms":{"default":false,"type":"boolean"},"email":{"type":"string"},"firstName":{"type":"string"},"fundsOrigin":{"items":{"type":"string"},"type":"array"},"fundsOriginOther":{"type":"string"},"isForeignCitizen":{"default":false,"type":"boolean"},"isPep":{"default":false,"type":"boolean"},"lastName":{"type":"string"},"nationality":{"maxLength":3,"pattern":"^[A-Z]+$","type":"string"},"otherTaxingCountry":{"nullable":true,"type":"string"},"pepDescription":{"nullable":true,"type":"string"},"phone":{"nullable":true,"pattern":"^[0-9-+ ]+$","type":"string"},"ssn":{"maxLength":11,"nullable":true,"pattern":"^[0-9]{11}$","type":"string"},"taxIdentityNumber":{"nullable":true,"type":"string"}},"required":["firstName","lastName","ssn","phone","email","nationality","fundsOrigin","isPep","isForeignCitizen","consentTerms","consentMarketing"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/product/security_deposit/{id}/kyc', {
			id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {boolean} data.body.consentMarketing  Default: [0]
	 * @param {boolean} data.body.isForeignCitizen  Default: [0]
	 * @param {boolean} data.body.isPep  Default: [0]
	 * @param {string} data.body.nationality 
	 * @param {string} data.body.otherTaxingCountry 
	 * @param {string} data.body.pepDescription 
	 * @param {string} data.body.taxIdentityNumber 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postSecurityDepositLandlordKCYForm}
	 * @returns {Promise} A promise
	 */
	postSecurityDepositLandlordKCYForm(id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postSecurityDepositLandlordKCYForm" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"consentMarketing":{"default":false,"type":"boolean"},"isForeignCitizen":{"default":false,"type":"boolean"},"isPep":{"default":false,"type":"boolean"},"nationality":{"maxLength":3,"pattern":"^[A-Z]+$","type":"string"},"otherTaxingCountry":{"nullable":true,"type":"string"},"pepDescription":{"nullable":true,"type":"string"},"taxIdentityNumber":{"nullable":true,"type":"string"}},"required":["nationality","isPep","isForeignCitizen","consentMarketing"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/product/security_deposit/{id}/landlord_kyc', {
			id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {boolean} data.body.acceptedSplit 
	 * @param {string} data.body.tenantAccountNumber 

	 * @see {@link file:///./redoc-husleieapi.html#operation/releaseDepositAccountV2}
	 * @returns {Promise} A promise
	 */
	releaseDepositAccountV2(id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "releaseDepositAccountV2" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"acceptedSplit":{"type":"boolean"},"tenantAccountNumber":{"maxLength":13,"minLength":11,"nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/product/deposit_account/{id}/release/v2', {
			id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * ProfilesLandlord
 * @extends HusleieAPI2
 */
export class ProfilesLandlordApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "ProfilesLandlord"...');

	}


	/**
	 *
	 * @param {} landlord_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.email 
	 * @param {string} data.body.name 
	 * @param {string} data.body.role  Enum: ['user', 'api']

	 * @see {@link file:///./redoc-husleieapi.html#operation/addProfileLandlordUser}
	 * @returns {Promise} A promise
	 */
	addProfileLandlordUser(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "addProfileLandlordUser" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"email":{"format":"email","type":"string"},"name":{"type":"string"},"role":{"enum":["user","api"],"type":"string"}},"required":["email"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/profiles/landlords/{landlord_id}/users', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteProfileLandlord}
	 * @returns {Promise} A promise
	 */
	deleteProfileLandlord(landlord_id, responseType = 'json') {

		this.logger('Calling API function "deleteProfileLandlord" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/profiles/landlords/{landlord_id}', {
			landlord_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} bankaccount_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteProfileLandlordBankAccount}
	 * @returns {Promise} A promise
	 */
	deleteProfileLandlordBankAccount(landlord_id, bankaccount_id, responseType = 'json') {

		this.logger('Calling API function "deleteProfileLandlordBankAccount" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/profiles/landlords/{landlord_id}/bankaccounts/{bankaccount_id}', {
			landlord_id,
			bankaccount_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} setting
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteProfileLandlordSetting}
	 * @returns {Promise} A promise
	 */
	deleteProfileLandlordSetting(landlord_id, setting, responseType = 'json') {

		this.logger('Calling API function "deleteProfileLandlordSetting" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/profiles/landlords/{landlord_id}/settings/{setting}', {
			landlord_id,
			setting,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} user_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteProfileLandlordUser}
	 * @returns {Promise} A promise
	 */
	deleteProfileLandlordUser(landlord_id, user_id, responseType = 'json') {

		this.logger('Calling API function "deleteProfileLandlordUser" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/profiles/landlords/{landlord_id}/users/{user_id}', {
			landlord_id,
			user_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandLordSummary}
	 * @returns {Promise} A promise
	 */
	getLandLordSummary(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getLandLordSummary" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}/summary', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlord}
	 * @returns {Promise} A promise
	 */
	getProfileLandlord(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlord" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlordBankAccounts}
	 * @returns {Promise} A promise
	 */
	getProfileLandlordBankAccounts(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlordBankAccounts" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}/bankaccounts', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlordDefaults}
	 * @returns {Promise} A promise
	 */
	getProfileLandlordDefaults(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlordDefaults" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}/defaults', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} invoice_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlordInvoice}
	 * @returns {Promise} A promise
	 */
	getProfileLandlordInvoice(landlord_id, invoice_id, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlordInvoice" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}/invoices/{invoice_id}', {
			landlord_id,
			invoice_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlordInvoices}
	 * @returns {Promise} A promise
	 */
	getProfileLandlordInvoices(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlordInvoices" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}/invoices', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} setting
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlordSetting}
	 * @returns {Promise} A promise
	 */
	getProfileLandlordSetting(landlord_id, setting, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlordSetting" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}/settings/{setting}', {
			landlord_id,
			setting,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlordSettings}
	 * @returns {Promise} A promise
	 */
	getProfileLandlordSettings(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlordSettings" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}/settings', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlordSubscriptions}
	 * @returns {Promise} A promise
	 */
	getProfileLandlordSubscriptions(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlordSubscriptions" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}/subscriptions', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileLandlordUsers}
	 * @returns {Promise} A promise
	 */
	getProfileLandlordUsers(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getProfileLandlordUsers" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/landlords/{landlord_id}/users', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.page {}Which page to display, only respected if limit has been specified
	 * @see {@link file:///./redoc-husleieapi.html#operation/listProfileLandlords}
	 * @returns {Promise} A promise
	 */
	listProfileLandlords({ query } = {}, responseType = 'json') {

		this.logger('Calling API function "listProfileLandlords" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/landlords';

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.acknowledgementToken UUID token for acknowledging a verification
	 * @param {array} data.body.addresses 
	 * @param {string} data.body.addresses[].careOf If postal and C/O aka care of is required
	 * @param {string} data.body.addresses[].countryCode ISO 3166-1 alpha-2 country code for postal address Default: [NO]
	 * @param {string} data.body.addresses[].postalArea Postal area
	 * @param {string} data.body.addresses[].postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.addresses[].postalZipcode Postal Zipcode
	 * @param {string} data.body.addresses[].purpose Enumeration of the purpose of the address entry Enum: ['postal', 'billing', 'residential', 'business'] Default: [residential]
	 * @param {string} data.body.addresses[].street Street name and number
	 * @param {integer} data.body.id 
	 * @param {object} data.body.meta 
	 * @param {boolean} data.body.meta.isIdentified 
	 * @param {boolean} data.body.meta.isIndexed 
	 * @param {boolean} data.body.meta.isProfessional 
	 * @param {object} data.body.meta.needsAcknowledgement 
	 * @param {integer} data.body.meta.needsAcknowledgement.id 
	 * @param {string} data.body.meta.needsAcknowledgement.landlordName 
	 * @param {object} data.body.meta.needsAcknowledgement.nationalIdentityNumber 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.error 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.previousValue 
	 * @param {object} data.body.meta.needsAcknowledgement.organizationNumber 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.error 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.previousValue 
	 * @param {array} data.body.meta.needsAcknowledgement.rentalObjects 
	 * @param {integer} data.body.meta.needsAcknowledgement.rentalObjects[].id 
	 * @param {array} data.body.meta.needsAcknowledgement.rentalObjects[].issues 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].error 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].name 
	 * @param {} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].previousValue 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].name 
	 * @param {string} data.body.meta.needsAcknowledgement.token UUID token for tracking verification acknowledgements
	 * @param {object} data.body.meta.organization 
	 * @param {boolean} data.body.meta.organization.isBankrupt True if business enterprise register says it's been registered as bankrupt
	 * @param {boolean} data.body.meta.organization.isRegisteredBusiness True if is registered in business enterprise register, Norway: Foretaksregister
	 * @param {boolean} data.body.meta.organization.isRegisteredForVAT True if business is registered in Value Added Tax register, Norway: Momsregister
	 * @param {string} data.body.meta.organization.name 
	 * @param {string} data.body.name 
	 * @param {string} data.body.nationalIdentityNumber National Identity Number, also known as Personnummer, D-nummer or the Norwegian social security number
	 * @param {string} data.body.organizationNumber "Hoved organisasjons nummer" at Brønnøysund/brreg.no
	 * @param {object} data.body.owner 
	 * @param {integer} data.body.owner.id 
	 * @param {boolean} data.body.owner.isMe 
	 * @param {string} data.body.owner.name 
	 * @param {string} data.body.profilePicture 
	 * @param {object} data.body.stats 
	 * @param {integer} data.body.stats.rentalObjectCount  Default: [0]
	 * @param {integer} data.body.stats.tenancyCount  Default: [0]

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchProfileLandlord}
	 * @returns {Promise} A promise
	 */
	patchProfileLandlord(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchProfileLandlord" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"acknowledgementToken":{"description":"UUID token for acknowledging a verification","nullable":true,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"addresses":{"items":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"type":"array"},"id":{"readOnly":true,"type":"integer"},"meta":{"properties":{"isIdentified":{"nullable":true,"type":"boolean"},"isIndexed":{"nullable":true,"type":"boolean"},"isProfessional":{"nullable":true,"type":"boolean"},"needsAcknowledgement":{"properties":{"id":{"type":"integer"},"landlordName":{"type":"string"},"nationalIdentityNumber":{"properties":{"error":{"type":"string"},"newValue":{"nullable":true,"type":"string"},"previousValue":{"nullable":true,"type":"string"}},"type":"object"},"organizationNumber":{"properties":{"error":{"type":"string"},"newValue":{"nullable":true,"type":"string"},"previousValue":{"nullable":true,"type":"string"}},"type":"object"},"rentalObjects":{"items":{"properties":{"id":{"type":"integer"},"issues":{"items":{"properties":{"error":{"type":"string"},"name":{"type":"string"},"newValue":{"oneOf":[{"nullable":true,"type":"string"},{"items":{"type":"string"},"type":"array"}]},"previousValue":{"nullable":true,"type":"string"}},"required":["name","error","newValue"],"title":"rentalObjectAcknowledgementField","type":"object"},"type":"array"},"name":{"type":"string"}},"required":["id"],"title":"rentalObjectAcknowledgement","type":"object"},"type":"array"},"token":{"description":"UUID token for tracking verification acknowledgements","nullable":true,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"}},"title":"landlordAcknowledgement","type":"object"},"organization":{"properties":{"isBankrupt":{"description":"True if business enterprise register says it's been registered as bankrupt","nullable":true,"type":"boolean"},"isRegisteredBusiness":{"description":"True if is registered in business enterprise register, Norway: Foretaksregister","nullable":true,"type":"boolean"},"isRegisteredForVAT":{"description":"True if business is registered in Value Added Tax register, Norway: Momsregister","nullable":true,"type":"boolean"},"name":{"nullable":true,"type":"string"}},"type":"object"}},"readOnly":true,"type":"object"},"name":{"type":"string"},"nationalIdentityNumber":{"description":"National Identity Number, also known as Personnummer, D-nummer or the Norwegian social security number","nullable":true,"type":"string"},"organizationNumber":{"description":"\"Hoved organisasjons nummer\" at BrÃ¸nnÃ¸ysund/brreg.no","nullable":true,"type":"string"},"owner":{"properties":{"id":{"type":"integer"},"isMe":{"readOnly":true,"type":"boolean"},"name":{"type":"string"}},"readOnly":true,"type":"object"},"profilePicture":{"readOnly":true,"type":"string"},"stats":{"properties":{"rentalObjectCount":{"default":"0","minimum":0,"type":"integer"},"tenancyCount":{"default":"0","minimum":0,"type":"integer"}},"readOnly":true,"type":"object"}},"title":"ProfileLandlord","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/profiles/landlords/{landlord_id}', {
			landlord_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {array} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchProfileLandlordDefaults}
	 * @returns {Promise} A promise
	 */
	patchProfileLandlordDefaults(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchProfileLandlordDefaults" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"items":{"properties":{"key":{"type":"string"},"value":{"nullable":true,"type":"string"}},"title":"KeyValue","type":"object"},"type":"array"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/profiles/landlords/{landlord_id}/defaults', {
			landlord_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.acknowledgementToken UUID token for acknowledging a verification
	 * @param {array} data.body.addresses 
	 * @param {string} data.body.addresses[].careOf If postal and C/O aka care of is required
	 * @param {string} data.body.addresses[].countryCode ISO 3166-1 alpha-2 country code for postal address Default: [NO]
	 * @param {string} data.body.addresses[].postalArea Postal area
	 * @param {string} data.body.addresses[].postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.addresses[].postalZipcode Postal Zipcode
	 * @param {string} data.body.addresses[].purpose Enumeration of the purpose of the address entry Enum: ['postal', 'billing', 'residential', 'business'] Default: [residential]
	 * @param {string} data.body.addresses[].street Street name and number
	 * @param {integer} data.body.id 
	 * @param {object} data.body.meta 
	 * @param {boolean} data.body.meta.isIdentified 
	 * @param {boolean} data.body.meta.isIndexed 
	 * @param {boolean} data.body.meta.isProfessional 
	 * @param {object} data.body.meta.needsAcknowledgement 
	 * @param {integer} data.body.meta.needsAcknowledgement.id 
	 * @param {string} data.body.meta.needsAcknowledgement.landlordName 
	 * @param {object} data.body.meta.needsAcknowledgement.nationalIdentityNumber 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.error 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.nationalIdentityNumber.previousValue 
	 * @param {object} data.body.meta.needsAcknowledgement.organizationNumber 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.error 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.organizationNumber.previousValue 
	 * @param {array} data.body.meta.needsAcknowledgement.rentalObjects 
	 * @param {integer} data.body.meta.needsAcknowledgement.rentalObjects[].id 
	 * @param {array} data.body.meta.needsAcknowledgement.rentalObjects[].issues 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].error 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].name 
	 * @param {} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].newValue 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].issues[].previousValue 
	 * @param {string} data.body.meta.needsAcknowledgement.rentalObjects[].name 
	 * @param {string} data.body.meta.needsAcknowledgement.token UUID token for tracking verification acknowledgements
	 * @param {object} data.body.meta.organization 
	 * @param {boolean} data.body.meta.organization.isBankrupt True if business enterprise register says it's been registered as bankrupt
	 * @param {boolean} data.body.meta.organization.isRegisteredBusiness True if is registered in business enterprise register, Norway: Foretaksregister
	 * @param {boolean} data.body.meta.organization.isRegisteredForVAT True if business is registered in Value Added Tax register, Norway: Momsregister
	 * @param {string} data.body.meta.organization.name 
	 * @param {string} data.body.name 
	 * @param {string} data.body.nationalIdentityNumber National Identity Number, also known as Personnummer, D-nummer or the Norwegian social security number
	 * @param {string} data.body.organizationNumber "Hoved organisasjons nummer" at Brønnøysund/brreg.no
	 * @param {object} data.body.owner 
	 * @param {integer} data.body.owner.id 
	 * @param {boolean} data.body.owner.isMe 
	 * @param {string} data.body.owner.name 
	 * @param {string} data.body.profilePicture 
	 * @param {object} data.body.stats 
	 * @param {integer} data.body.stats.rentalObjectCount  Default: [0]
	 * @param {integer} data.body.stats.tenancyCount  Default: [0]

	 * @see {@link file:///./redoc-husleieapi.html#operation/postProfileLandlord}
	 * @returns {Promise} A promise
	 */
	postProfileLandlord({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "postProfileLandlord" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"acknowledgementToken":{"description":"UUID token for acknowledging a verification","nullable":true,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"addresses":{"items":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"type":"array"},"id":{"readOnly":true,"type":"integer"},"meta":{"properties":{"isIdentified":{"nullable":true,"type":"boolean"},"isIndexed":{"nullable":true,"type":"boolean"},"isProfessional":{"nullable":true,"type":"boolean"},"needsAcknowledgement":{"properties":{"id":{"type":"integer"},"landlordName":{"type":"string"},"nationalIdentityNumber":{"properties":{"error":{"type":"string"},"newValue":{"nullable":true,"type":"string"},"previousValue":{"nullable":true,"type":"string"}},"type":"object"},"organizationNumber":{"properties":{"error":{"type":"string"},"newValue":{"nullable":true,"type":"string"},"previousValue":{"nullable":true,"type":"string"}},"type":"object"},"rentalObjects":{"items":{"properties":{"id":{"type":"integer"},"issues":{"items":{"properties":{"error":{"type":"string"},"name":{"type":"string"},"newValue":{"oneOf":[{"nullable":true,"type":"string"},{"items":{"type":"string"},"type":"array"}]},"previousValue":{"nullable":true,"type":"string"}},"required":["name","error","newValue"],"title":"rentalObjectAcknowledgementField","type":"object"},"type":"array"},"name":{"type":"string"}},"required":["id"],"title":"rentalObjectAcknowledgement","type":"object"},"type":"array"},"token":{"description":"UUID token for tracking verification acknowledgements","nullable":true,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"}},"title":"landlordAcknowledgement","type":"object"},"organization":{"properties":{"isBankrupt":{"description":"True if business enterprise register says it's been registered as bankrupt","nullable":true,"type":"boolean"},"isRegisteredBusiness":{"description":"True if is registered in business enterprise register, Norway: Foretaksregister","nullable":true,"type":"boolean"},"isRegisteredForVAT":{"description":"True if business is registered in Value Added Tax register, Norway: Momsregister","nullable":true,"type":"boolean"},"name":{"nullable":true,"type":"string"}},"type":"object"}},"readOnly":true,"type":"object"},"name":{"type":"string"},"nationalIdentityNumber":{"description":"National Identity Number, also known as Personnummer, D-nummer or the Norwegian social security number","nullable":true,"type":"string"},"organizationNumber":{"description":"\"Hoved organisasjons nummer\" at BrÃ¸nnÃ¸ysund/brreg.no","nullable":true,"type":"string"},"owner":{"properties":{"id":{"type":"integer"},"isMe":{"readOnly":true,"type":"boolean"},"name":{"type":"string"}},"readOnly":true,"type":"object"},"profilePicture":{"readOnly":true,"type":"string"},"stats":{"properties":{"rentalObjectCount":{"default":"0","minimum":0,"type":"integer"},"tenancyCount":{"default":"0","minimum":0,"type":"integer"}},"readOnly":true,"type":"object"}},"title":"ProfileLandlord","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/landlords';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} bankaccount_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.alias 
	 * @param {object} data.body.bank 
	 * @param {string} data.body.bank.BIC 
	 * @param {string} data.body.bank.logo 
	 * @param {string} data.body.bank.name 
	 * @param {string} data.body.bank.prefix 
	 * @param {string} data.body.bankAccount 
	 * @param {string} data.body.id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/setProfileLandlordBankAccount}
	 * @returns {Promise} A promise
	 */
	setProfileLandlordBankAccount(landlord_id, bankaccount_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "setProfileLandlordBankAccount" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"alias":{"type":"string"},"bank":{"properties":{"BIC":{"type":"string"},"logo":{"nullable":true,"type":"string"},"name":{"type":"string"},"prefix":{"type":"string"}},"readOnly":true,"type":"object"},"bankAccount":{"type":"string"},"id":{"type":"string"}},"title":"BankAccount","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/profiles/landlords/{landlord_id}/bankaccounts/{bankaccount_id}', {
			landlord_id,
			bankaccount_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.key 
	 * @param {string} data.body.value 

	 * @see {@link file:///./redoc-husleieapi.html#operation/setProfileLandlordDefault}
	 * @returns {Promise} A promise
	 */
	setProfileLandlordDefault(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "setProfileLandlordDefault" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"key":{"type":"string"},"value":{"nullable":true,"type":"string"}},"title":"KeyValue","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/profiles/landlords/{landlord_id}/defaults', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} setting
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/setProfileLandlordSetting}
	 * @returns {Promise} A promise
	 */
	setProfileLandlordSetting(landlord_id, setting, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "setProfileLandlordSetting" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"anyOf":[{"type":"string"},{"type":"number"},{"type":"integer"},{"type":"boolean"},{"items":{},"type":"array"},{"properties":{},"type":"object"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/profiles/landlords/{landlord_id}/settings/{setting}', {
			landlord_id,
			setting,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * ProfilesUser
 * @extends HusleieAPI2
 */
export class ProfilesUserApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "ProfilesUser"...');

	}


	/**
	 *
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileUser}
	 * @returns {Promise} A promise
	 */
	getProfileUser(responseType = 'json') {

		this.logger('Calling API function "getProfileUser" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/user';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} token
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileUserPasswordToken}
	 * @returns {Promise} A promise
	 */
	getProfileUserPasswordToken(token, responseType = 'json') {

		this.logger('Calling API function "getProfileUserPasswordToken" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/profiles/user/password/token/{token}', {
			token,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * # Mypage Pending Activity Actions

| **Action type**          | **Menu string** | **Description**                        |
| ------------------------ | --------------- | -------------------------------------- |
| top_sign                 | Signer          | Redirect the user to the external url. |
| top_edit                 | Rediger         | Redirect the user to the external url. |
| top_delete               | Slett           | ?                                      |
| contract_edit            | Rediger         | Redirect the user to the external url. |
| contract_delete          | Slett           | Call `deleteContractSession()`         |
| contract_cancel_signing  | Stopp signering | Call cancelContractSigning`()`         |
| contract_landlord_sign   | Signer          | Redirect the user to the external url. |
| lease_termination_edit   | Rediger         | Redirect the user to the external url. |
| lease_termination_delete | Slett           | Call `deleteLeaseTerminationSession()` |
| letting_edit             | Rediger         | Redirect the user to the external url. |
| letting_delete           | Slett           | Call `deleteLetting()`                 |
| deposit_instabank_sign   | Signer          | Redirect the user to the external url. |

	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.fromDate {}Return activities modified after this date. If not given, this will be set to toDate -30 days
	 * @param data.query.toDate {}Return only activities up and including this date. If not given, it is set to todays date.
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileUserPendingActivities}
	 * @returns {Promise} A promise
	 */
	getProfileUserPendingActivities({ query } = {}, responseType = 'json') {

		this.logger('Calling API function "getProfileUserPendingActivities" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/user/pending_activities';

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileUserWhoAmI}
	 * @returns {Promise} A promise
	 */
	getProfileUserWhoAmI(responseType = 'json') {

		this.logger('Calling API function "getProfileUserWhoAmI" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/user/whoami';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @see {@link file:///./redoc-husleieapi.html#operation/listLandlordTaxReportMissingIdentity}
	 * @returns {Promise} A promise
	 */
	listLandlordTaxReportMissingIdentity(responseType = 'json') {

		this.logger('Calling API function "listLandlordTaxReportMissingIdentity" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/user/taxreport/missing_identity';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.cellPhone Users cell phone. See the verified object, and seperate API-methods for performing the validation.
	 * @param {string} data.body.dateOfBirth 
	 * @param {string} data.body.email Unique email. Updating this field can fail if there is already a user with this email in the system. See the verified object, and seperate API-methods for performing the validation.
	 * @param {string} data.body.name Full name
	 * @param {array} data.body.userRoles 
	 * @param {object} data.body.verified This object contains keys that will be defined with a date to indicate when the verification was completed. This field is read-only.
	 * @param {string} data.body.verified.cellPhone This field is read-only.
	 * @param {string} data.body.verified.email This field is read-only.

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchProfileUser}
	 * @returns {Promise} A promise
	 */
	patchProfileUser({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchProfileUser" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"cellPhone":{"description":"Users cell phone. See the verified object, and seperate API-methods for performing the validation.","maxLength":20,"type":"string"},"dateOfBirth":{"format":"date","type":"string"},"email":{"description":"Unique email. Updating this field can fail if there is already a user with this email in the system. See the verified object, and seperate API-methods for performing the validation.","format":"email","maxLength":255,"type":"string"},"name":{"description":"Full name","maxLength":120,"type":"string"},"userRoles":{"items":{"enum":["tenant","landlord","administrator","partner_api"],"type":"string"},"readOnly":true,"type":"array"},"verified":{"description":"This object contains keys that will be defined with a date to indicate when the verification was completed. This field is read-only.","properties":{"cellPhone":{"description":"This field is read-only.","format":"date-time","type":"string"},"email":{"description":"This field is read-only.","format":"date-time","type":"string"}},"readOnly":true,"type":"object"}},"title":"ProfileUser","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user';

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.context The context property is used to choose which email template to use when resending the verification mail. Enum: ['signup', 'profile', 'reverify'] Default: [profile]
	 * @param {string} data.body.email If user has provided a new email, the user profile will be updated with the new email, and a new mail verification mail will be sent.

	 * @see {@link file:///./redoc-husleieapi.html#operation/profileUserTriggerNewEmailVerificationEmail}
	 * @returns {Promise} A promise
	 */
	profileUserTriggerNewEmailVerificationEmail({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "profileUserTriggerNewEmailVerificationEmail" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"context":{"default":"profile","description":"The context property is used to choose which email template to use when resending the verification mail.","enum":["signup","profile","reverify"],"type":"string"},"email":{"description":"If user has provided a new email, the user profile will be updated with the new email, and a new mail verification mail will be sent.","format":"email","maxLength":255,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/verify/email/trigger';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.pinCode 

	 * @see {@link file:///./redoc-husleieapi.html#operation/profileUserVerifyCellPhone}
	 * @returns {Promise} A promise
	 */
	profileUserVerifyCellPhone({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "profileUserVerifyCellPhone" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"pinCode":{"pattern":"^\\d{5}$","type":"string"}},"required":["pinCode"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/verify/cellphone';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.token 

	 * @see {@link file:///./redoc-husleieapi.html#operation/profileUserVerifyEmail}
	 * @returns {Promise} A promise
	 */
	profileUserVerifyEmail({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "profileUserVerifyEmail" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"token":{"maxLength":36,"minLength":36,"type":"string"}},"required":["token"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/verify/email';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.currentPassword 
	 * @param {string} data.body.newPassword 

	 * @see {@link file:///./redoc-husleieapi.html#operation/setProfileUserPassword}
	 * @returns {Promise} A promise
	 */
	setProfileUserPassword({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "setProfileUserPassword" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"currentPassword":{"type":"string"},"newPassword":{"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/password';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/setProfileUserPreference}
	 * @returns {Promise} A promise
	 */
	setProfileUserPreference({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "setProfileUserPreference" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"oneOf":[{"properties":{"key":{"nullable":false,"type":"string"},"value":{"nullable":true,"type":"string"}},"title":"KeyValueObject","type":"object"},{"items":{"properties":{"key":{"nullable":false,"type":"string"},"value":{"nullable":true,"type":"string"}},"title":"KeyValueObject","type":"object"},"title":"KeyValueObjectArray","type":"array"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/profiles/user/preference';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * ProfilesUserMyPage
 * @extends HusleieAPI2
 */
export class ProfilesUserMyPageApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "ProfilesUserMyPage"...');

	}


	/**
	 *
	 * @see {@link file:///./redoc-husleieapi.html#operation/getProfileUserMyPageLettings}
	 * @returns {Promise} A promise
	 */
	getProfileUserMyPageLettings(responseType = 'json') {

		this.logger('Calling API function "getProfileUserMyPageLettings" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/profiles/user/mypage/lettings';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * QA
 * @extends HusleieAPI2
 */
export class QAApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "QA"...');

	}


	/**
	 *
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/qaGetSecurityDeposit}
	 * @returns {Promise} A promise
	 */
	qaGetSecurityDeposit(session_id, responseType = 'json') {

		this.logger('Calling API function "qaGetSecurityDeposit" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/qa/security_deposit/{session_id}', {
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/qaGetSecurityDepositVendorStatus}
	 * @returns {Promise} A promise
	 */
	qaGetSecurityDepositVendorStatus(session_id, responseType = 'json') {

		this.logger('Calling API function "qaGetSecurityDepositVendorStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/qa/security_deposit/{session_id}/vendor_status', {
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.signAs Sign as either tenant of landlord Enum: ['tenant', 'landlord']

	 * @see {@link file:///./redoc-husleieapi.html#operation/qaPostContractSessionSign}
	 * @returns {Promise} A promise
	 */
	qaPostContractSessionSign(session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "qaPostContractSessionSign" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"signAs":{"description":"Sign as either tenant of landlord","enum":["tenant","landlord"],"nullable":false,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/qa/contract_session/{session_id}/sign', {
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/qaPostDepositAccountSign}
	 * @returns {Promise} A promise
	 */
	qaPostDepositAccountSign(session_id, responseType = 'json') {

		this.logger('Calling API function "qaPostDepositAccountSign" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/qa/deposit_account/{session_id}/sign', {
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.vendorStatus Vendor status

	 * @see {@link file:///./redoc-husleieapi.html#operation/qaPostSecurityDepositVendorStatus}
	 * @returns {Promise} A promise
	 */
	qaPostSecurityDepositVendorStatus(session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "qaPostSecurityDepositVendorStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"vendorStatus":{"description":"Vendor status","nullable":false,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/qa/security_deposit/{session_id}/vendor_status', {
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.lastUpdate Search for mandrill logs after the last update date
	 * @param {integer} data.body.maxRows Max rows to return Default: [10]
	 * @param {string} data.body.receiver Email address of the receiver
	 * @param {string} data.body.template Search for mandrill logs that match a template
	 * @param {object} data.body.vars search for vars object

	 * @see {@link file:///./redoc-husleieapi.html#operation/qaSearchMandrillLogs}
	 * @returns {Promise} A promise
	 */
	qaSearchMandrillLogs({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "qaSearchMandrillLogs" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"example":{"lastUpdate":"2022-12-01T12:01:01","maxRows":100,"receiver":"example@email.com","template":"template_name","vars":{"landlord_id":50}},"properties":{"lastUpdate":{"description":"Search for mandrill logs after the last update date","format":"date-time","type":"string"},"maxRows":{"default":"10","description":"Max rows to return","type":"integer"},"receiver":{"description":"Email address of the receiver","type":"string"},"template":{"description":"Search for mandrill logs that match a template","type":"string"},"vars":{"description":"search for vars object","type":"object"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/qa/search_mandrill_logs';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/qaSecurityDepositRefretchVendorData}
	 * @returns {Promise} A promise
	 */
	qaSecurityDepositRefretchVendorData(session_id, responseType = 'json') {

		this.logger('Calling API function "qaSecurityDepositRefretchVendorData" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/qa/security_deposit/{session_id}/refetch_vendor_data', {
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/qaSession}
	 * @returns {Promise} A promise
	 */
	qaSession(session_id, responseType = 'json') {

		this.logger('Calling API function "qaSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/qa/session/{session_id}', {
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.created Create time
	 * @param {string} data.body.id ID of the Session
	 * @param {object} data.body.meta Meta data
	 * @param {string} data.body.modified Last modified time
	 * @param {object} data.body.object Object data
	 * @param {string} data.body.type Type of session store

	 * @see {@link file:///./redoc-husleieapi.html#operation/qaUpdateSession}
	 * @returns {Promise} A promise
	 */
	qaUpdateSession(session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "qaUpdateSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"created":{"description":"Create time","nullable":false,"type":"string"},"id":{"description":"ID of the Session","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"meta":{"description":"Meta data","nullable":true,"type":"object"},"modified":{"description":"Last modified time","nullable":false,"type":"string"},"object":{"description":"Object data","nullable":true,"type":"object"},"type":{"description":"Type of session store","nullable":false,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/qa/session/{session_id}', {
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * RentalObject
 * @extends HusleieAPI2
 */
export class RentalObjectApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "RentalObject"...');

	}


	/**
	 * Remove one rental object
	 * @param {} landlord_id Required Landlord ID
	 * @param {} rental_object_id Required Rental Object ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordRentalObject}
	 * @returns {Promise} A promise
	 */
	deleteLandlordRentalObject(landlord_id, rental_object_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordRentalObject" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/rental_objects/{rental_object_id}', {
			landlord_id,
			rental_object_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get a specific rental objects and attached tenancies
	 * @param {} landlord_id Required Landlord ID
	 * @param {} rental_object_id Required Rental Object ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.tenancyStatus {}Which status of the tenancies should be listed
	 * @param data.query.extended {}Return extended information about the rental object
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordRentalObject}
	 * @returns {Promise} A promise
	 */
	getLandlordRentalObject(landlord_id, rental_object_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordRentalObject" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/rental_objects/{rental_object_id}', {
			landlord_id,
			rental_object_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * List rental objects for specified landlord and attached tenancies
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.sortBy {}Which column should we sort by
	 * @param data.query.condensed {}Set it to true if a very condensed rental objects list should be retured
	 * @param data.query.tenancyStatus {}Which status of the tenancies should be listed
	 * @param data.query.page {}Which page to display, only respected if limit has been specified
	 * @param data.query.limit {}Limit amount of items returned, if 0 everything will be returned
	 * @param data.query.sortByOrder {}Which order column should we sort by
	 * @param data.query.query {}Only list elements that contains specified string
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordRentalObjects}
	 * @returns {Promise} A promise
	 */
	getLandlordRentalObjects(landlord_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordRentalObjects" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/rental_objects', {
			landlord_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get a specific rental objects and attached tenancies
	 * @param {} landlord_id Required Landlord ID
	 * @param {} rental_object_id Required Rental Object ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.acknowledgementField UUID token for acknowledging a verification
	 * @param {string} data.body.acknowledgementToken UUID token for acknowledging a verification
	 * @param {object} data.body.address 
	 * @param {string} data.body.address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [NO]
	 * @param {string} data.body.address.postalArea Postal area
	 * @param {string} data.body.address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.address.postalZipcode Postal Zipcode
	 * @param {string} data.body.address.purpose Enumeration of the purpose of the address entry Enum: ['postal', 'billing', 'residential', 'business'] Default: [residential]
	 * @param {string} data.body.address.street Street name and number
	 * @param {object} data.body.details 
	 * @param {string} data.body.details.alias 
	 * @param {boolean} data.body.details.animalsAllowed  Default: [0]
	 * @param {string} data.body.details.apartmentNumber 
	 * @param {string} data.body.details.areaSize 
	 * @param {integer} data.body.details.belongsToBuildingId 
	 * @param {string} data.body.details.buildingId 
	 * @param {string} data.body.details.comment 
	 * @param {object} data.body.details.ctaUris 
	 * @param {string} data.body.details.electricityMeter 
	 * @param {string} data.body.details.electricityMeterId 
	 * @param {string} data.body.details.energyScore  Enum: ['Ikke valgt', 'A', 'B', 'C', 'D', 'E', 'F']
	 * @param {array} data.body.details.facilities 
	 * @param {boolean} data.body.details.facilities[].checked 
	 * @param {string} data.body.details.facilities[].name  Enum: ['aircondition', 'barnevennlig', 'garasje', 'kabel_tv', 'parkett', 'sentralt', 'turterreng', 'alarm', 'bredbånd', 'peis', 'heis', 'livløpsstandard', 'utsikt', 'balkong', 'fellesvaskeri', 'ingen_gjenboere', 'moderne', 'rolig', 'vaktmester']
	 * @param {integer} data.body.details.floor 
	 * @param {string} data.body.details.furnished  Enum: ['Ikke valgt', 'Umøblert', 'Delvis Møblert', 'Møblert']
	 * @param {string} data.body.details.heatingScore  Enum: ['Ikke valgt', 'Mørkegrønn', 'Lysegrønn', 'Gul', 'Oransje', 'Rød']
	 * @param {string} data.body.details.houseRules 
	 * @param {string} data.body.details.name 
	 * @param {boolean} data.body.details.professional_landlord 
	 * @param {string} data.body.details.profilePictureUri 
	 * @param {string} data.body.details.roomCount 
	 * @param {string} data.body.details.roomNumber 
	 * @param {array} data.body.details.rooms 
	 * @param {integer} data.body.details.rooms[].count 
	 * @param {boolean} data.body.details.rooms[].default 
	 * @param {string} data.body.details.rooms[].name 
	 * @param {boolean} data.body.details.rooms[].shared 
	 * @param {boolean} data.body.details.smokingAllowed  Default: [0]
	 * @param {string} data.body.details.specialRemarks 
	 * @param {string} data.body.details.type  Enum: ['apartment', 'house', 'co-op', 'basement flat', 'dorm', 'semi-detached', 'chained-house', 'commercial', 'garage-parking', 'other', 'unknown']
	 * @param {string} data.body.details.uri 
	 * @param {object} data.body.details.weight 
	 * @param {integer} data.body.details.weight.overall  Default: [100]
	 * @param {integer} data.body.details.weight.period  Default: [100]
	 * @param {array} data.body.tags 

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchLandlordRentalObject}
	 * @returns {Promise} A promise
	 */
	patchLandlordRentalObject(landlord_id, rental_object_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchLandlordRentalObject" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"acknowledgementField":{"description":"UUID token for acknowledging a verification","pattern":"^rentalObject\\.(address|details)(\\..+)?$","type":"string"},"acknowledgementToken":{"description":"UUID token for acknowledging a verification","pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","type":"string"},"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"details":{"properties":{"alias":{"nullable":true,"type":"string"},"animalsAllowed":{"default":false,"nullable":true,"type":"boolean"},"apartmentNumber":{"nullable":true,"type":"string"},"areaSize":{"nullable":true,"type":"string"},"belongsToBuildingId":{"nullable":true,"type":"integer"},"buildingId":{"nullable":true,"type":"string"},"comment":{"nullable":true,"type":"string"},"ctaUris":{"type":"object"},"electricityMeter":{"nullable":true,"type":"string"},"electricityMeterId":{"nullable":true,"type":"string"},"energyScore":{"enum":["Ikke valgt","A","B","C","D","E","F"],"nullable":true,"type":"string"},"facilities":{"items":{"properties":{"checked":{"type":"boolean"},"name":{"enum":["aircondition","barnevennlig","garasje","kabel_tv","parkett","sentralt","turterreng","alarm","bredbÃ¥nd","peis","heis","livlÃ¸psstandard","utsikt","balkong","fellesvaskeri","ingen_gjenboere","moderne","rolig","vaktmester"],"type":"string"}},"type":"object"},"type":"array"},"floor":{"nullable":true,"type":"integer"},"furnished":{"enum":["Ikke valgt","UmÃ¸blert","Delvis MÃ¸blert","MÃ¸blert"],"nullable":true,"type":"string"},"heatingScore":{"enum":["Ikke valgt","MÃ¸rkegrÃ¸nn","LysegrÃ¸nn","Gul","Oransje","RÃ¸d"],"nullable":true,"type":"string"},"houseRules":{"nullable":true,"type":"string"},"name":{"nullable":true,"type":"string"},"professional_landlord":{"type":"boolean"},"profilePictureUri":{"nullable":true,"type":"string"},"roomCount":{"nullable":true,"type":"string"},"roomNumber":{"nullable":true,"type":"string"},"rooms":{"items":{"properties":{"count":{"type":"integer"},"default":{"type":"boolean"},"name":{"type":"string"},"shared":{"type":"boolean"}},"type":"object"},"type":"array"},"smokingAllowed":{"default":false,"nullable":true,"type":"boolean"},"specialRemarks":{"nullable":true,"type":"string"},"type":{"enum":["apartment","house","co-op","basement flat","dorm","semi-detached","chained-house","commercial","garage-parking","other","unknown"],"type":"string"},"uri":{"nullable":true,"type":"string"},"weight":{"properties":{"overall":{"default":"100","type":"integer"},"period":{"default":"100","type":"integer"}},"type":"object"}},"type":"object"},"tags":{"items":{"type":"string"},"type":"array"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/rental_objects/{rental_object_id}', {
			landlord_id,
			rental_object_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new rental object in the database and returns the id of the new object
	 * @param {} landlord_id Required Landlord ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.alias 
	 * @param {string} data.body.apartmentNumber 
	 * @param {integer} data.body.belongsToBuildingId 
	 * @param {string} data.body.countryCode 
	 * @param {string} data.body.matrikkel_id 
	 * @param {string} data.body.postal_area 
	 * @param {string} data.body.postal_zipcode 
	 * @param {string} data.body.roomNumber 
	 * @param {string} data.body.street 
	 * @param {string} data.body.type  Enum: ['apartment', 'house', 'co-op', 'basement flat', 'dorm', 'semi-detached', 'chained-house', 'commercial', 'garage-parking', 'other', 'unknown']

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordRentalObject}
	 * @returns {Promise} A promise
	 */
	postLandlordRentalObject(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordRentalObject" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"alias":{"nullable":true,"type":"string"},"apartmentNumber":{"nullable":true,"type":"string"},"belongsToBuildingId":{"nullable":true,"type":"integer"},"countryCode":{"nullable":false,"pattern":"^NO$","type":"string"},"matrikkel_id":{"nullable":true,"type":"string"},"postal_area":{"nullable":true,"type":"string"},"postal_zipcode":{"nullable":true,"type":"string"},"roomNumber":{"nullable":true,"type":"string"},"street":{"nullable":false,"type":"string"},"type":{"enum":["apartment","house","co-op","basement flat","dorm","semi-detached","chained-house","commercial","garage-parking","other","unknown"],"type":"string"}},"required":["countryCode"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/rental_objects', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Service
 * @extends HusleieAPI2
 */
export class ServiceApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Service"...');

	}


	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.nationalIdentityNumber National Identity Number aka Social Security Number of person we're 
	 * @param {string} data.body.token JWT token

	 * @see {@link file:///./redoc-husleieapi.html#operation/doCreditCheck}
	 * @returns {Promise} A promise
	 */
	doCreditCheck({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "doCreditCheck" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"example":{"nationalIdentityNumber":"11111111111","token":"xxxxx.yyyyy.zzzzz"},"properties":{"nationalIdentityNumber":{"description":"National Identity Number aka Social Security Number of person we're ","nullable":false,"type":"string"},"token":{"description":"JWT token","nullable":false,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/services/creditcheck';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.filename Filename of PDF
	 * @param {string} data.body.html HTML to render
	 * @param {integer} data.body.rental_object_id Rental Object ID to upload PDF to
	 * @param {integer} data.body.tenancy_id Tenancy ID to upload PDF to

	 * @see {@link file:///./redoc-husleieapi.html#operation/renderPDF}
	 * @returns {Promise} A promise
	 */
	renderPDF({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "renderPDF" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"example":{"filename":"document.pdf","html":"<html><body><h1>Hello World</h1></body></html>","tenancy_id":123},"properties":{"filename":{"description":"Filename of PDF","nullable":true,"type":"string"},"html":{"description":"HTML to render","nullable":false,"type":"string"},"rental_object_id":{"description":"Rental Object ID to upload PDF to","nullable":true,"type":"integer"},"tenancy_id":{"description":"Tenancy ID to upload PDF to","nullable":true,"type":"integer"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/services/pdf/render';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * SessionStore
 * @extends HusleieAPI2
 */
export class SessionStoreApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "SessionStore"...');

	}


	/**
	 * Returns a list of possible landlord upsell opportunities for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/LandlordUpsellOpportunities}
	 * @returns {Promise} A promise
	 */
	LandlordUpsellOpportunities(type, session_id, responseType = 'json') {

		this.logger('Calling API function "LandlordUpsellOpportunities" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/landlord/opportunities', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returned prepared landlord upsell data for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {} upsell_type Required upsell type
	 * @see {@link file:///./redoc-husleieapi.html#operation/PrepareLandlordUpsell}
	 * @returns {Promise} A promise
	 */
	PrepareLandlordUpsell(type, session_id, upsell_type, responseType = 'json') {

		this.logger('Calling API function "PrepareLandlordUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/landlord/opportunity/{upsell_type}', {
			type,
			session_id,
			upsell_type,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returned prepared tenant upsell data for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {} upsell_type Required upsell type
	 * @see {@link file:///./redoc-husleieapi.html#operation/PrepareTenantUpsell}
	 * @returns {Promise} A promise
	 */
	PrepareTenantUpsell(type, session_id, upsell_type, responseType = 'json') {

		this.logger('Calling API function "PrepareTenantUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/tenant/opportunity/{upsell_type}', {
			type,
			session_id,
			upsell_type,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns a list of possible tenant upsell opportunities for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/TenantUpsellOpportunities}
	 * @returns {Promise} A promise
	 */
	TenantUpsellOpportunities(type, session_id, responseType = 'json') {

		this.logger('Calling API function "TenantUpsellOpportunities" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/tenant/opportunities', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new session object with role {type} that the client can use to store the session information.
	 * @param {} type Required session type
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createSession}
	 * @returns {Promise} A promise
	 */
	createSession(type, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"oneOf":[{"properties":{"session_id":{"type":"string"}},"required":["session_id"],"type":"object"},{"properties":{"tenancy_id":{"type":"string"}},"required":["tenancy_id"],"type":"object"},{"properties":{"rental_object_id":{"type":"string"}},"required":["rental_object_id"],"type":"object"},{"properties":{"landlord_id":{"type":"string"}},"required":["landlord_id"],"type":"object"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}', {
			type,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Decision of tenant upsell opportunity
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {} upsell_type Required upsell type
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.decision  Enum: ['accepted', 'declined']
	 * @param {string} data.body.upsell_id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/decisionTenantUpsell}
	 * @returns {Promise} A promise
	 */
	decisionTenantUpsell(type, session_id, upsell_type, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "decisionTenantUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"decision":{"enum":["accepted","declined"],"type":"string"},"upsell_id":{"nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}/upsell/tenant/opportunity/{upsell_type}', {
			type,
			session_id,
			upsell_type,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Delete a session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteSession}
	 * @returns {Promise} A promise
	 */
	deleteSession(type, session_id, responseType = 'json') {

		this.logger('Calling API function "deleteSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/session/{type}/{session_id}', {
			type,
			session_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Generates and offers the session as a HTML- or PDF-file for download. See download_token for generating the required token.
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.token {}
	 * @param data.query.format {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/downloadSession}
	 * @returns {Promise} A promise
	 */
	downloadSession(type, session_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "downloadSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/download', {
			type,
			session_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns an object with a token that can be used to download a PDF-version of the session. A url that can be used directly is also provided.
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getDownloadTokenByJWT}
	 * @returns {Promise} A promise
	 */
	getDownloadTokenByJWT(type, session_id, responseType = 'json') {

		this.logger('Calling API function "getDownloadTokenByJWT" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/download_token', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns an object with a token that can be used to download a PDF-version of the session. A url that can be used directly is also provided.
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.pin 

	 * @see {@link file:///./redoc-husleieapi.html#operation/getDownloadTokenByPIN}
	 * @returns {Promise} A promise
	 */
	getDownloadTokenByPIN(type, session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "getDownloadTokenByPIN" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"pin":{"type":"string"}},"required":["pin"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}/download_token', {
			type,
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the content of a session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSession}
	 * @returns {Promise} A promise
	 */
	getSession(type, session_id, responseType = 'json') {

		this.logger('Calling API function "getSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the signing method available for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSigningMethods}
	 * @returns {Promise} A promise
	 */
	getSigningMethods(type, session_id, responseType = 'json') {

		this.logger('Calling API function "getSigningMethods" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/signing_methods', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Handle a session callback
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {array} data.body.nextSigners 
	 * @param {object} data.body.nextSigners[].details 
	 * @param {string} data.body.nextSigners[].details.errorRedirectURL Might be used by the signing method implementation to redirect the user after a failed signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.
	 * @param {string} data.body.nextSigners[].details.successRedirectURL Might be used by the signing method implementation to redirect the user after a successful signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.
	 * @param {string} data.body.nextSigners[].id 
	 * @param {object} data.body.nextSigners[].signer 
	 * @param {object} data.body.nextSigners[].signer.address 
	 * @param {string} data.body.nextSigners[].signer.address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.nextSigners[].signer.address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [NO]
	 * @param {string} data.body.nextSigners[].signer.address.postalArea Postal area
	 * @param {string} data.body.nextSigners[].signer.address.postalCode Postal Zipcode
	 * @param {string} data.body.nextSigners[].signer.address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.nextSigners[].signer.address.purpose Enumeration of the purpose of the address entry Enum: ['postal', 'billing', 'residential', 'business'] Default: [residential]
	 * @param {string} data.body.nextSigners[].signer.address.street Street name and number
	 * @param {string} data.body.nextSigners[].signer.cellPhone 
	 * @param {string} data.body.nextSigners[].signer.dateOfBirth The entity date of birth, in ISO-8601 format
	 * @param {string} data.body.nextSigners[].signer.email 
	 * @param {string} data.body.nextSigners[].signer.familyName 
	 * @param {string} data.body.nextSigners[].signer.givenName 
	 * @param {string} data.body.nextSigners[].signer.placeOfBirth The entity place of birth
	 * @param {object} data.body.nextSigners[].signer.title 
	 * @param {string} data.body.nextSigners[].signer.title.common  Enum: ['DIVERSE', 'MSS', 'MR']
	 * @param {string} data.body.nextSigners[].signer.title.professional  Enum: ['PROF', 'DR', 'PROF_DR']
	 * @param {string} data.body.nextSigners[].status  Enum: ['waiting', 'pending', 'signed', 'declined']
	 * @param {string} data.body.nextSigners[].type  Enum: ['landlord', 'tenant', 'co-tenant', 'bail', 'witness']
	 * @param {string} data.body.signedToken 
	 * @param {string} data.body.signingSessionId 
	 * @param {string} data.body.step  Enum: ['create', 'add_signature', 'finalize', 'delete']

	 * @param data.query {object}
	 * @param data.query.token {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/handleSessionCallback}
	 * @returns {Promise} A promise
	 */
	handleSessionCallback(type, session_id, { body, query } = {}, responseType = 'json') {

		this.logger('Calling API function "handleSessionCallback" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"nextSigners":{"items":{"additionalProperties":false,"properties":{"details":{"properties":{"errorRedirectURL":{"description":"Might be used by the signing method implementation to redirect the user after a failed signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.","type":"string"},"successRedirectURL":{"description":"Might be used by the signing method implementation to redirect the user after a successful signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.","type":"string"}},"type":"object"},"id":{"type":"string"},"signer":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":false,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":true,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","cellPhone"],"type":"object"},"status":{"enum":["waiting","pending","signed","declined"],"type":"string"},"type":{"enum":["landlord","tenant","co-tenant","bail","witness"],"type":"string"}},"required":["type","signer"],"type":"object"},"type":"array"},"signedToken":{"type":"string"},"signingSessionId":{"type":"string"},"step":{"enum":["create","add_signature","finalize","delete"],"type":"string"}},"required":["signingSessionId","step","nextSigners"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}/callback', {
			type,
			session_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the necessary data to create a signing session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.signingMethod  Enum: ['offline', 'token', 'verified_digital_signing']

	 * @see {@link file:///./redoc-husleieapi.html#operation/prepareSigning}
	 * @returns {Promise} A promise
	 */
	prepareSigning(type, session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "prepareSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"signingMethod":{"enum":["offline","token","verified_digital_signing"],"type":"string"}},"required":["signingMethod"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}/prepare_signing', {
			type,
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returned prepared tenant upsell data for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {} upsell_type Required upsell type
	 * @see {@link file:///./redoc-husleieapi.html#operation/prepareUpsell}
	 * @returns {Promise} A promise
	 */
	prepareUpsell(type, session_id, upsell_type, responseType = 'json') {

		this.logger('Calling API function "prepareUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/opportunity/{upsell_type}', {
			type,
			session_id,
			upsell_type,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Generates and offers the session as a HTML-, PDF- or JSON-file for preview
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.format {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/previewSession}
	 * @returns {Promise} A promise
	 */
	previewSession(type, session_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "previewSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/preview', {
			type,
			session_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Update a session, replacing the content of the session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateSession}
	 * @returns {Promise} A promise
	 */
	updateSession(type, session_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateSession" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"oneOf":[{"properties":{"coTenants":{"items":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":false,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":true,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","cellPhone"],"type":"object"},"type":"array"},"inspectionReport":{"properties":{"comment":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"}},"type":"object"},"landlord":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":false,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":true,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","cellPhone"],"type":"object"},"rentalObject":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"category":{"enum":["APARTMENT"],"type":"string"},"details":{"properties":{"amountOfResidents":{"type":"integer"},"moveInDate":{"type":"string"}},"type":"object"},"furnishingStandard":{"enum":["FURNISHED","UNFURNISHED"],"type":"string"},"id":{"type":"integer"},"keychain":{"properties":{"comment":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"keys":{"items":{"properties":{"name":{"type":"string"},"quantity":{"type":"integer"}},"required":["name","quantity"],"type":"object"},"type":"array"}},"type":"object"},"rooms":{"items":{"properties":{"comment":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"name":{"type":"string"}},"required":["name"],"type":"object"},"type":"array"},"utilities":{"properties":{"coldWater":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"},"electricity":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"},"gas":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"},"heating":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"},"hotWater":{"properties":{"meter":{"properties":{"number":{"type":"string"},"reading":{"properties":{"comment":{"type":"string"},"date":{"type":"string"},"images":{"items":{"properties":{"token":{"type":"string"}},"required":["token"],"type":"object"},"type":"array"},"value":{"type":"number"}},"type":"object"}},"type":"object"},"state":{"enum":["RELEVANT","NOT_RELEVANT"],"type":"string"}},"required":["state"],"type":"object"}},"type":"object"}},"required":["category","address"],"type":"object"},"tenant":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":false,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":true,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","cellPhone"],"type":"object"},"type":{"enum":["MOVE_IN","MOVE_OUT","INSPECTION"],"type":"string"}},"type":"object"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/session/{type}/{session_id}', {
			type,
			session_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns a list of possible tenant upsell opportunities for this session
	 * @param {} type Required session type
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/upsellOpportunities}
	 * @returns {Promise} A promise
	 */
	upsellOpportunities(type, session_id, responseType = 'json') {

		this.logger('Calling API function "upsellOpportunities" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/session/{type}/{session_id}/upsell/opportunities', {
			type,
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Signing
 * @extends HusleieAPI2
 */
export class SigningApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Signing"...');

	}


	/**
	 * Add a signature to the session
	 * @param {} session_id Required session id
	 * @param {} signer_id Required signer id
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/addSignature}
	 * @returns {Promise} A promise
	 */
	addSignature(session_id, signer_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "addSignature" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"anyOf":[{"additionalProperties":false,"properties":{},"type":"object"},{"properties":{"signatureFileToken":{"type":"string"}},"type":"object"},{"properties":{},"type":"object"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/signing/{session_id}/{signer_id}', {
			session_id,
			signer_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Creates a new signing session.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.callback 
	 * @param {string} data.body.method  Enum: ['offline', 'token', 'verified_digital_signing']
	 * @param {string} data.body.representative 
	 * @param {array} data.body.signers 
	 * @param {string} data.body.token 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createSigning}
	 * @returns {Promise} A promise
	 */
	createSigning({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"callback":{"type":"string"},"method":{"enum":["offline","token","verified_digital_signing"],"type":"string"},"representative":{"type":"string"},"signers":{"items":{"items":{"additionalProperties":false,"properties":{"details":{"properties":{"errorRedirectURL":{"description":"Might be used by the signing method implementation to redirect the user after a failed signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.","type":"string"},"successRedirectURL":{"description":"Might be used by the signing method implementation to redirect the user after a successful signing. The patterns {{singing_id}} and {{signer_id}} will be replaced.","type":"string"}},"type":"object"},"id":{"type":"string"},"signer":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalCode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalCode"],"type":"object"},"cellPhone":{"nullable":false,"type":"string"},"dateOfBirth":{"description":"The entity date of birth, in ISO-8601 format","format":"date","nullable":true,"type":"string"},"email":{"nullable":true,"type":"string"},"familyName":{"nullable":false,"type":"string"},"givenName":{"nullable":false,"type":"string"},"placeOfBirth":{"description":"The entity place of birth","nullable":true,"type":"string"},"title":{"properties":{"common":{"enum":["DIVERSE","MSS","MR"],"nullable":true,"type":"string"},"professional":{"enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"required":["givenName","familyName","cellPhone"],"type":"object"},"status":{"enum":["waiting","pending","signed","declined"],"type":"string"},"type":{"enum":["landlord","tenant","co-tenant","bail","witness"],"type":"string"}},"required":["type","signer"],"type":"object"},"type":"array"},"type":"array"},"token":{"type":"string"}},"required":["method","token","signers"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/signing';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Cancel and delete an ongoing signing session
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteSigning}
	 * @returns {Promise} A promise
	 */
	deleteSigning(session_id, responseType = 'json') {

		this.logger('Calling API function "deleteSigning" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/signing/{session_id}', {
			session_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the state of the signer
	 * @param {} session_id Required session id
	 * @param {} signer_id Required signer id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSignerStatus}
	 * @returns {Promise} A promise
	 */
	getSignerStatus(session_id, signer_id, responseType = 'json') {

		this.logger('Calling API function "getSignerStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/signing/{session_id}/{signer_id}', {
			session_id,
			signer_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get status of a signing session.
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getSigningStatus}
	 * @returns {Promise} A promise
	 */
	getSigningStatus(session_id, responseType = 'json') {

		this.logger('Calling API function "getSigningStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/signing/{session_id}', {
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Returns the PDF file to be signed
	 * @param {} session_id Required session id
	 * @see {@link file:///./redoc-husleieapi.html#operation/previewDocument}
	 * @returns {Promise} A promise
	 */
	previewDocument(session_id, responseType = 'json') {

		this.logger('Calling API function "previewDocument" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/signing/{session_id}/preview', {
			session_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Handles a Nect signing event callback. This endpoint expects a `Authorization` header providing a JWT token with a valid `nect_token` claim.
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.documentId 
	 * @param {string} data.body.documentReference 
	 * @param {string} data.body.reason 
	 * @param {string} data.body.signatoryId 
	 * @param {string} data.body.signatoryReference 
	 * @param {boolean} data.body.success 
	 * @param {string} data.body.time 
	 * @param {string} data.body.type  Enum: ['Created', 'Accessed', 'Identifying', 'Identified', 'Opened', 'Signed', 'Finished', 'Deleted']

	 * @see {@link file:///./redoc-husleieapi.html#operation/signingCallbackNect}
	 * @returns {Promise} A promise
	 */
	signingCallbackNect({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "signingCallbackNect" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"documentId":{"type":"string"},"documentReference":{"type":"string"},"reason":{"type":"string"},"signatoryId":{"type":"string"},"signatoryReference":{"type":"string"},"success":{"type":"boolean"},"time":{"type":"string"},"type":{"enum":["Created","Accessed","Identifying","Identified","Opened","Signed","Finished","Deleted"],"type":"string"}},"required":["type","time","documentId","success","reason"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/signing/callback/nect';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Signup
 * @extends HusleieAPI2
 */
export class SignupApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Signup"...');

	}


	/**
	 * Check if given email is in use
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.email Unique email

	 * @see {@link file:///./redoc-husleieapi.html#operation/checkEmailInUse}
	 * @returns {Promise} A promise
	 */
	checkEmailInUse({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "checkEmailInUse" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"email":{"description":"Unique email","format":"email","maxLength":255,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/signup/email/in/use';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Endpoint to create user through signup funnel
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.cellPhone Users cell phone. See the verified object, and seperate API-methods for performing the validation.
	 * @param {string} data.body.dateOfBirth 
	 * @param {string} data.body.email Unique email
	 * @param {string} data.body.familyName Family name
	 * @param {string} data.body.givenName Given name
	 * @param {string} data.body.language Language in ISO 639-1 format, for example nb-NO
	 * @param {string} data.body.password Password
	 * @param {string} data.body.role  Enum: ['tenant', 'landlord']
	 * @param {string} data.body.targetAction Target action after signup, only values consisting of a-z and A-Z are allowed
	 * @param {object} data.body.terms 
	 * @param {boolean} data.body.terms.GDPR 
	 * @param {boolean} data.body.terms.emailConsent 
	 * @param {boolean} data.body.terms.terms 
	 * @param {boolean} data.body.terms.termsAndPrivacyStatement 
	 * @param {string} data.body.timezone Time zone in TZ database name format, for example Europe/Oslo
	 * @param {object} data.body.title 
	 * @param {string} data.body.title.common From English honorifics, Mr, Mrs, Miss etc Enum: ['MR', 'MSS', 'DIVERSE']
	 * @param {string} data.body.title.professional From English honorifics, academic and professional, Dr, Professor etc  Enum: ['PROF', 'DR', 'PROF_DR']

	 * @see {@link file:///./redoc-husleieapi.html#operation/createUser}
	 * @returns {Promise} A promise
	 */
	createUser({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "createUser" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"cellPhone":{"description":"Users cell phone. See the verified object, and seperate API-methods for performing the validation.","maxLength":20,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"description":"Unique email","format":"email","maxLength":255,"type":"string"},"familyName":{"description":"Family name","maxLength":120,"type":"string"},"givenName":{"description":"Given name","maxLength":120,"type":"string"},"language":{"description":"Language in ISO 639-1 format, for example nb-NO","nullable":true,"type":"string"},"password":{"description":"Password","maxLength":255,"minLength":8,"type":"string"},"role":{"enum":["tenant","landlord"],"type":"string"},"targetAction":{"description":"Target action after signup, only values consisting of a-z and A-Z are allowed","pattern":"^[a-zA-Z]+$","type":"string"},"terms":{"properties":{"GDPR":{"nullable":true,"type":"boolean"},"emailConsent":{"nullable":true,"type":"boolean"},"terms":{"nullable":true,"type":"boolean"},"termsAndPrivacyStatement":{"nullable":true,"type":"boolean"}},"type":"object"},"timezone":{"description":"Time zone in TZ database name format, for example Europe/Oslo","nullable":true,"type":"string"},"title":{"properties":{"common":{"description":"From English honorifics, Mr, Mrs, Miss etc","enum":["MR","MSS","DIVERSE"],"nullable":true,"type":"string"},"professional":{"description":"From English honorifics, academic and professional, Dr, Professor etc ","enum":["PROF","DR","PROF_DR"],"nullable":true,"type":"string"}},"type":"object"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/signup/user';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * TakeoverProtocol
 * @extends HusleieAPI2
 */
export class TakeoverProtocolApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "TakeoverProtocol"...');

	}


	/**
	 * Delete a takeover protocol
	 * @param {} top_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteTOPSession}
	 * @returns {Promise} A promise
	 */
	deleteTOPSession(top_id, responseType = 'json') {

		this.logger('Calling API function "deleteTOPSession" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/top_session/{top_id}', {
			top_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Tenancy
 * @extends HusleieAPI2
 */
export class TenancyApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Tenancy"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.category  Enum: ['contract', 'depgar', 'takeover_protocol', 'inspection_report', 'contract_downloaded_for_offline_signing', 'rent_adjustment_letter', 'other'] Default: [other]
	 * @param {string} data.body.contentType 
	 * @param {string} data.body.created 
	 * @param {boolean} data.body.deletable 
	 * @param {string} data.body.fileName 
	 * @param {integer} data.body.id 
	 * @param {array} data.body.mediaLinks 
	 * @param {string} data.body.mediaLinks[].contentType 
	 * @param {string} data.body.mediaLinks[].type  Enum: ['original', 'thumbnail']
	 * @param {string} data.body.mediaLinks[].url 
	 * @param {integer} data.body.mediaLinks[].width 
	 * @param {integer} data.body.size 
	 * @param {string} data.body.token 

	 * @see {@link file:///./redoc-husleieapi.html#operation/addLandlordTenancyDocument}
	 * @returns {Promise} A promise
	 */
	addLandlordTenancyDocument(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "addLandlordTenancyDocument" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"category":{"default":"other","enum":["contract","depgar","takeover_protocol","inspection_report","contract_downloaded_for_offline_signing","rent_adjustment_letter","other"],"type":"string"},"contentType":{"readOnly":true,"type":"string"},"created":{"format":"date-time","readOnly":true,"type":"string"},"deletable":{"type":"boolean"},"fileName":{"readOnly":true,"type":"string"},"id":{"readOnly":true,"type":"integer"},"mediaLinks":{"items":{"properties":{"contentType":{"type":"string"},"type":{"enum":["original","thumbnail"],"type":"string"},"url":{"type":"string"},"width":{"type":"integer"}},"required":["type","contentType","url"],"type":"object"},"readOnly":true,"title":"LettingMediaLinks","type":"array"},"size":{"readOnly":true,"type":"integer"},"token":{"type":"string"}},"title":"TenancyDocument","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/documents', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/addLandlordTenancySubProduct}
	 * @returns {Promise} A promise
	 */
	addLandlordTenancySubProduct(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "addLandlordTenancySubProduct" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"allOf":[{"properties":{"category":{"default":2,"type":"integer"},"effectiveDate":{"format":"date","nullable":true,"type":"string"},"invoiceInterval":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"name":{"type":"string"},"price":{"type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancySubProductBarebone","type":"object"}],"properties":{"id":{"type":"integer"}},"title":"TenancySubProduct","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.address 
	 * @param {string} data.body.address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [NO]
	 * @param {string} data.body.address.postalArea Postal area
	 * @param {string} data.body.address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.address.postalZipcode Postal Zipcode
	 * @param {string} data.body.address.purpose Enumeration of the purpose of the address entry Enum: ['postal', 'billing', 'residential', 'business'] Default: [residential]
	 * @param {string} data.body.address.street Street name and number
	 * @param {string} data.body.cellPhone 
	 * @param {string} data.body.dateOfBirth 
	 * @param {string} data.body.email 
	 * @param {string} data.body.fullName 
	 * @param {string} data.body.id 
	 * @param {string} data.body.organizationName 
	 * @param {string} data.body.organizationNumber 
	 * @param {string} data.body.type  Enum: ['tenant', 'cotenant'] Default: [tenant]

	 * @see {@link file:///./redoc-husleieapi.html#operation/addLandlordTenancyTenant}
	 * @returns {Promise} A promise
	 */
	addLandlordTenancyTenant(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "addLandlordTenancyTenant" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"type":"string"},"fullName":{"type":"string"},"id":{"type":"string"},"organizationName":{"type":"string"},"organizationNumber":{"type":"string"},"type":{"default":"tenant","enum":["tenant","cotenant"],"type":"string"}},"title":"TenancyTenant","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} deposit_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/cancelDepositSubscription}
	 * @returns {Promise} A promise
	 */
	cancelDepositSubscription(landlord_id, tenancy_id, deposit_id, responseType = 'json') {

		this.logger('Calling API function "cancelDepositSubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/deposits/{deposit_id}/cancel_subscription', {
			landlord_id,
			tenancy_id,
			deposit_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/cancelLandlordTenancyCPIAdjustment}
	 * @returns {Promise} A promise
	 */
	cancelLandlordTenancyCPIAdjustment(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "cancelLandlordTenancyCPIAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/CPIAdjustment', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/cancelTenancySubscription}
	 * @returns {Promise} A promise
	 */
	cancelTenancySubscription(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "cancelTenancySubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/subscription/cancel', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {array} data.body.coTenants 
	 * @param {object} data.body.coTenants[].address 
	 * @param {string} data.body.coTenants[].address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.coTenants[].address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [NO]
	 * @param {string} data.body.coTenants[].address.postalArea Postal area
	 * @param {string} data.body.coTenants[].address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.coTenants[].address.postalZipcode Postal Zipcode
	 * @param {string} data.body.coTenants[].address.purpose Enumeration of the purpose of the address entry Enum: ['postal', 'billing', 'residential', 'business'] Default: [residential]
	 * @param {string} data.body.coTenants[].address.street Street name and number
	 * @param {string} data.body.coTenants[].cellPhone 
	 * @param {string} data.body.coTenants[].dateOfBirth 
	 * @param {string} data.body.coTenants[].email 
	 * @param {string} data.body.coTenants[].fullName 
	 * @param {string} data.body.coTenants[].id 
	 * @param {string} data.body.coTenants[].organizationName 
	 * @param {string} data.body.coTenants[].organizationNumber 
	 * @param {string} data.body.coTenants[].type  Enum: ['tenant', 'cotenant'] Default: [tenant]
	 * @param {object} data.body.details 
	 * @param {integer} data.body.details.bindingPeriod 
	 * @param {string} data.body.details.customClientId 
	 * @param {integer} data.body.details.noticePeriod 
	 * @param {string} data.body.details.poNumber 
	 * @param {string} data.body.endDate 
	 * @param {object} data.body.rent 
	 * @param {string} data.body.rent.bankAccount 
	 * @param {number} data.body.rent.billableRent Total billable rent including sub-products
	 * @param {integer} data.body.rent.bindingPeriod 
	 * @param {number} data.body.rent.dueDay  Enum: ['1', '5', '10', '15', '20', '25']
	 * @param {string} data.body.rent.duePeriod  Enum: ['advance', 'current', 'postpay']
	 * @param {string} data.body.rent.invoicePeriod  Enum: ['month', 'bimonth', 'quarter', 'biquarter', 'annual', 'semi-anual'] Default: [month]
	 * @param {number} data.body.rent.rent Tenancy rent
	 * @param {integer} data.body.rent.vat 
	 * @param {object} data.body.rentAdjustment 
	 * @param {boolean} data.body.rentAdjustment.CPIAdjustment  Default: [0]
	 * @param {boolean} data.body.rentAdjustment.inProgress  Default: [0]
	 * @param {string} data.body.rentAdjustment.lastCPIAdjustment 
	 * @param {integer} data.body.rentalObjectId 
	 * @param {string} data.body.startDate 
	 * @param {array} data.body.subProducts 
	 * @param {integer} data.body.subProducts[].category  Default: [2]
	 * @param {string} data.body.subProducts[].effectiveDate 
	 * @param {string} data.body.subProducts[].invoiceInterval  Enum: ['month', 'bimonth', 'quarter', 'biquarter', 'annual', 'semi-anual'] Default: [month]
	 * @param {string} data.body.subProducts[].name 
	 * @param {number} data.body.subProducts[].price 
	 * @param {integer} data.body.subProducts[].vat 
	 * @param {object} data.body.subscription 
	 * @param {object} data.body.subscription.payment 
	 * @param {object} data.body.subscription.payment.stripe 
	 * @param {string} data.body.subscription.payment.stripe.paymentMethodId 
	 * @param {number} data.body.subscription.price 
	 * @param {string} data.body.subscription.priceCategory  Enum: ['currency', 'percentage', 'units']
	 * @param {integer} data.body.subscription.subscriptionPlanId 
	 * @param {string} data.body.subscription.subscriptionPlanName 
	 * @param {object} data.body.tenant 
	 * @param {object} data.body.tenant.address 
	 * @param {string} data.body.tenant.address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.tenant.address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [NO]
	 * @param {string} data.body.tenant.address.postalArea Postal area
	 * @param {string} data.body.tenant.address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.tenant.address.postalZipcode Postal Zipcode
	 * @param {string} data.body.tenant.address.purpose Enumeration of the purpose of the address entry Enum: ['postal', 'billing', 'residential', 'business'] Default: [residential]
	 * @param {string} data.body.tenant.address.street Street name and number
	 * @param {string} data.body.tenant.cellPhone 
	 * @param {string} data.body.tenant.dateOfBirth 
	 * @param {string} data.body.tenant.email 
	 * @param {string} data.body.tenant.fullName 
	 * @param {string} data.body.tenant.id 
	 * @param {string} data.body.tenant.organizationName 
	 * @param {string} data.body.tenant.organizationNumber 
	 * @param {string} data.body.tenant.type  Enum: ['tenant', 'cotenant'] Default: [tenant]

	 * @see {@link file:///./redoc-husleieapi.html#operation/createLandlordTenancy}
	 * @returns {Promise} A promise
	 */
	createLandlordTenancy(landlord_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createLandlordTenancy" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"coTenants":{"items":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"type":"string"},"fullName":{"type":"string"},"id":{"type":"string"},"organizationName":{"type":"string"},"organizationNumber":{"type":"string"},"type":{"default":"tenant","enum":["tenant","cotenant"],"type":"string"}},"title":"TenancyTenant","type":"object"},"type":"array"},"details":{"properties":{"bindingPeriod":{"nullable":true,"type":"integer"},"customClientId":{"nullable":true,"type":"string"},"noticePeriod":{"nullable":true,"type":"integer"},"poNumber":{"nullable":true,"type":"string"}},"type":"object"},"endDate":{"format":"date","nullable":true,"type":"string"},"rent":{"properties":{"bankAccount":{"nullable":true,"type":"string"},"billableRent":{"description":"Total billable rent including sub-products","type":"number"},"bindingPeriod":{"maximum":36,"minimum":0,"nullable":true,"type":"integer"},"dueDay":{"enum":["1","5","10","15","20","25"],"type":"number"},"duePeriod":{"enum":["advance","current","postpay"],"type":"string"},"invoicePeriod":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"rent":{"description":"Tenancy rent","type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancyRent","type":"object"},"rentAdjustment":{"nullable":true,"properties":{"CPIAdjustment":{"default":false,"type":"boolean"},"inProgress":{"default":false,"type":"boolean"},"lastCPIAdjustment":{"format":"date","type":"string"}},"title":"TenancyRentAdjustment","type":"object"},"rentalObjectId":{"type":"integer"},"startDate":{"format":"date","type":"string"},"subProducts":{"items":{"properties":{"category":{"default":"2","type":"integer"},"effectiveDate":{"format":"date","nullable":true,"type":"string"},"invoiceInterval":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"name":{"type":"string"},"price":{"type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancySubProductBarebone","type":"object"},"type":"array"},"subscription":{"properties":{"payment":{"properties":{"stripe":{"properties":{"paymentMethodId":{"type":"string"}},"type":"object"}},"type":"object"},"price":{"readOnly":true,"type":"number"},"priceCategory":{"enum":["currency","percentage","units"],"readOnly":true,"type":"string"},"subscriptionPlanId":{"type":"integer"},"subscriptionPlanName":{"readOnly":true,"type":"string"}},"title":"TenancySubscription","type":"object"},"tenant":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"type":"string"},"fullName":{"type":"string"},"id":{"type":"string"},"organizationName":{"type":"string"},"organizationNumber":{"type":"string"},"type":{"default":"tenant","enum":["tenant","cotenant"],"type":"string"}},"title":"TenancyTenant","type":"object"}},"title":"Create Tenancy","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies', {
			landlord_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.effectiveDate 
	 * @param {boolean} data.body.isMietCheck 
	 * @param {object} data.body.meta 
	 * @param {number} data.body.newRent 
	 * @param {string} data.body.notificationDate 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createTenancyRentAdjustment}
	 * @returns {Promise} A promise
	 */
	createTenancyRentAdjustment(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createTenancyRentAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"effectiveDate":{"format":"date","type":"string"},"isMietCheck":{"type":"boolean"},"meta":{"type":"object"},"newRent":{"format":"float","minimum":1,"type":"number"},"notificationDate":{"format":"date","nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancy}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancy(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancy" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} deposit_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancyDeposit}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancyDeposit(landlord_id, tenancy_id, deposit_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancyDeposit" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/deposits/{deposit_id}', {
			landlord_id,
			tenancy_id,
			deposit_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} document_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancyDocument}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancyDocument(landlord_id, tenancy_id, document_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancyDocument" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/documents/{document_id}', {
			landlord_id,
			tenancy_id,
			document_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} note_id Required Tenancy Note ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancyNote}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancyNote(landlord_id, tenancy_id, note_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancyNote" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/notes/{note_id}', {
			landlord_id,
			tenancy_id,
			note_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} subproduct_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancySubProduct}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancySubProduct(landlord_id, tenancy_id, subproduct_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancySubProduct" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts/{subproduct_id}', {
			landlord_id,
			tenancy_id,
			subproduct_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} tenant_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteLandlordTenancyTenant}
	 * @returns {Promise} A promise
	 */
	deleteLandlordTenancyTenant(landlord_id, tenancy_id, tenant_id, responseType = 'json') {

		this.logger('Calling API function "deleteLandlordTenancyTenant" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants/{tenant_id}', {
			landlord_id,
			tenancy_id,
			tenant_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} adjustment_id Required Rent Adjustment ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteTenancyRentAdjustment}
	 * @returns {Promise} A promise
	 */
	deleteTenancyRentAdjustment(landlord_id, tenancy_id, adjustment_id, responseType = 'json') {

		this.logger('Calling API function "deleteTenancyRentAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments/{adjustment_id}', {
			landlord_id,
			tenancy_id,
			adjustment_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.origin 
	 * @param {integer} data.body.subscriptionPlanId 

	 * @see {@link file:///./redoc-husleieapi.html#operation/downgradeTenancySubscription}
	 * @returns {Promise} A promise
	 */
	downgradeTenancySubscription(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "downgradeTenancySubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"origin":{"pattern":"^[a-z0-9_]+$","type":"string"},"subscriptionPlanId":{"type":"integer"}},"required":["subscriptionPlanId"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/subscription/downgrade', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getAllTenancyRentAdjustments}
	 * @returns {Promise} A promise
	 */
	getAllTenancyRentAdjustments(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getAllTenancyRentAdjustments" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancies}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancies(landlord_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancies" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies', {
			landlord_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancy}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancy(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancy" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyAvailableSubscriptions}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyAvailableSubscriptions(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyAvailableSubscriptions" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/available_subscriptions', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyCPIAdjustment}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyCPIAdjustment(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyCPIAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/CPIAdjustment', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} deposit_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyDeposit}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyDeposit(landlord_id, tenancy_id, deposit_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyDeposit" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/deposits/{deposit_id}', {
			landlord_id,
			tenancy_id,
			deposit_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyDeposits}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyDeposits(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyDeposits" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/deposits', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyDocuments}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyDocuments(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyDocuments" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/documents', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} note_id Required Tenancy Note ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyNote}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyNote(landlord_id, tenancy_id, note_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyNote" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/notes/{note_id}', {
			landlord_id,
			tenancy_id,
			note_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyNotes}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyNotes(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyNotes" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/notes', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.page {}Which page to display, only respected if limit has been specified
	 * @param data.query.limit {}Limit amount of items returned, if 0 everything will be returned
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyReminders}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyReminders(landlord_id, tenancy_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyReminders" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/reminders', {
			landlord_id,
			tenancy_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyRent}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyRent(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyRent" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/rent', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} subproduct_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancySubProduct}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancySubProduct(landlord_id, tenancy_id, subproduct_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancySubProduct" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts/{subproduct_id}', {
			landlord_id,
			tenancy_id,
			subproduct_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancySubProducts}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancySubProducts(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancySubProducts" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} tenant_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyTenant}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyTenant(landlord_id, tenancy_id, tenant_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyTenant" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants/{tenant_id}', {
			landlord_id,
			tenancy_id,
			tenant_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyTenants}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyTenants(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyTenants" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} adjustment_id Required Rent Adjustment ID
	 * @see {@link file:///./redoc-husleieapi.html#operation/getTenancyRentAdjustment}
	 * @returns {Promise} A promise
	 */
	getTenancyRentAdjustment(landlord_id, tenancy_id, adjustment_id, responseType = 'json') {

		this.logger('Calling API function "getTenancyRentAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments/{adjustment_id}', {
			landlord_id,
			tenancy_id,
			adjustment_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getTenancySubscriptionPlans}
	 * @returns {Promise} A promise
	 */
	getTenancySubscriptionPlans(landlord_id, tenancy_id, responseType = 'json') {

		this.logger('Calling API function "getTenancySubscriptionPlans" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/available_subscriptions', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} deposit_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.landlordBankAccountNumber 
	 * @param {string} data.body.onCancelledUrl URL to redirect to when the tenant has cancelled the release. Required for Instabank.
	 * @param {string} data.body.onErrorUrl URL to redirect to when an error has occured. Required for Instabank.
	 * @param {string} data.body.onSignedUrl URL to redirect to when the tenant has signed the release. Required for Instabank.
	 * @param {number} data.body.splitAmount 

	 * @see {@link file:///./redoc-husleieapi.html#operation/initiateLandlordTenancyDepositRelease}
	 * @returns {Promise} A promise
	 */
	initiateLandlordTenancyDepositRelease(landlord_id, tenancy_id, deposit_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "initiateLandlordTenancyDepositRelease" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"landlordBankAccountNumber":{"type":"string"},"onCancelledUrl":{"description":"URL to redirect to when the tenant has cancelled the release. Required for Instabank.","type":"string"},"onErrorUrl":{"description":"URL to redirect to when an error has occured. Required for Instabank.","type":"string"},"onSignedUrl":{"description":"URL to redirect to when the tenant has signed the release. Required for Instabank.","type":"string"},"splitAmount":{"type":"number"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/deposits/{deposit_id}/initiate_release', {
			landlord_id,
			tenancy_id,
			deposit_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} note_id Required Tenancy Note ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.author Name of the notetaker
	 * @param {integer} data.body.author_id ID of the notetaker
	 * @param {string} data.body.body The main body of the note
	 * @param {object} data.body.date 
	 * @param {string} data.body.date.created The UTC date and time of when note were created
	 * @param {string} data.body.date.edited The UTC date and time of when note were updated
	 * @param {string} data.body.id UUID of the note

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchLandlordTenancyNote}
	 * @returns {Promise} A promise
	 */
	patchLandlordTenancyNote(landlord_id, tenancy_id, note_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchLandlordTenancyNote" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"author":{"description":"Name of the notetaker","nullable":false,"readOnly":true,"type":"string"},"author_id":{"description":"ID of the notetaker","nullable":false,"readOnly":true,"type":"integer"},"body":{"description":"The main body of the note","nullable":false,"type":"string"},"date":{"properties":{"created":{"description":"The UTC date and time of when note were created","format":"date-time","nullable":false,"type":"string"},"edited":{"description":"The UTC date and time of when note were updated","format":"date-time","nullable":true,"type":"string"}},"readOnly":true,"type":"object"},"id":{"description":"UUID of the note","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","readOnly":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/notes/{note_id}', {
			landlord_id,
			tenancy_id,
			note_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} adjustment_id Required Rent Adjustment ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.effectiveDate 
	 * @param {boolean} data.body.isMietCheck 
	 * @param {object} data.body.meta 
	 * @param {number} data.body.newRent 
	 * @param {string} data.body.notificationDate 

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchTenancyRentAdjustment}
	 * @returns {Promise} A promise
	 */
	patchTenancyRentAdjustment(landlord_id, tenancy_id, adjustment_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchTenancyRentAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"effectiveDate":{"format":"date","type":"string"},"isMietCheck":{"type":"boolean"},"meta":{"type":"object"},"newRent":{"format":"float","minimum":1,"type":"number"},"notificationDate":{"format":"date","nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments/{adjustment_id}', {
			landlord_id,
			tenancy_id,
			adjustment_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.author Name of the notetaker
	 * @param {integer} data.body.author_id ID of the notetaker
	 * @param {string} data.body.body The main body of the note
	 * @param {object} data.body.date 
	 * @param {string} data.body.date.created The UTC date and time of when note were created
	 * @param {string} data.body.date.edited The UTC date and time of when note were updated
	 * @param {string} data.body.id UUID of the note

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordTenancyNote}
	 * @returns {Promise} A promise
	 */
	postLandlordTenancyNote(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordTenancyNote" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"author":{"description":"Name of the notetaker","nullable":false,"readOnly":true,"type":"string"},"author_id":{"description":"ID of the notetaker","nullable":false,"readOnly":true,"type":"integer"},"body":{"description":"The main body of the note","nullable":false,"type":"string"},"date":{"properties":{"created":{"description":"The UTC date and time of when note were created","format":"date-time","nullable":false,"type":"string"},"edited":{"description":"The UTC date and time of when note were updated","format":"date-time","nullable":true,"type":"string"}},"readOnly":true,"type":"object"},"id":{"description":"UUID of the note","nullable":false,"pattern":"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$","readOnly":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/notes', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.effectiveDate 
	 * @param {boolean} data.body.isMietCheck 
	 * @param {object} data.body.meta 
	 * @param {number} data.body.newRent 
	 * @param {string} data.body.notificationDate 

	 * @see {@link file:///./redoc-husleieapi.html#operation/previewTenancyRentAdjustmentLetter}
	 * @returns {Promise} A promise
	 */
	previewTenancyRentAdjustmentLetter(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "previewTenancyRentAdjustmentLetter" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"effectiveDate":{"format":"date","type":"string"},"isMietCheck":{"type":"boolean"},"meta":{"type":"object"},"newRent":{"format":"float","minimum":1,"type":"number"},"notificationDate":{"format":"date","nullable":true,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/rent/adjustments/preview/letter', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.details 
	 * @param {integer} data.body.details.bindingPeriod 
	 * @param {string} data.body.details.customClientId 
	 * @param {integer} data.body.details.noticePeriod 
	 * @param {string} data.body.details.poNumber 
	 * @param {string} data.body.endDate 
	 * @param {boolean} data.body.hasDeposits  Default: [0]
	 * @param {integer} data.body.id 
	 * @param {} data.body.rent 
	 * @param {} data.body.rentAdjustment 
	 * @param {integer} data.body.rentalObjectId 
	 * @param {object} data.body.settings 
	 * @param {string} data.body.settings.debt_collection  Enum: ['manual', 'automatic_debt_collection']
	 * @param {string} data.body.startDate 
	 * @param {string} data.body.status  Enum: ['active', 'free', 'resigned', 'deleted', 'suspended', 'duplicate', 'paused'] Default: [active]
	 * @param {array} data.body.subProducts 
	 * @param {integer} data.body.subProducts[].id 
	 * @param {} data.body.subscription 
	 * @param {array} data.body.tenants 
	 * @param {object} data.body.tenants[].address 
	 * @param {string} data.body.tenants[].address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.tenants[].address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [NO]
	 * @param {string} data.body.tenants[].address.postalArea Postal area
	 * @param {string} data.body.tenants[].address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.tenants[].address.postalZipcode Postal Zipcode
	 * @param {string} data.body.tenants[].address.purpose Enumeration of the purpose of the address entry Enum: ['postal', 'billing', 'residential', 'business'] Default: [residential]
	 * @param {string} data.body.tenants[].address.street Street name and number
	 * @param {string} data.body.tenants[].cellPhone 
	 * @param {string} data.body.tenants[].dateOfBirth 
	 * @param {string} data.body.tenants[].email 
	 * @param {string} data.body.tenants[].fullName 
	 * @param {string} data.body.tenants[].id 
	 * @param {string} data.body.tenants[].organizationName 
	 * @param {string} data.body.tenants[].organizationNumber 
	 * @param {string} data.body.tenants[].type  Enum: ['tenant', 'cotenant'] Default: [tenant]

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancy}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancy(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancy" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"details":{"properties":{"bindingPeriod":{"nullable":true,"type":"integer"},"customClientId":{"nullable":true,"type":"string"},"noticePeriod":{"nullable":true,"type":"integer"},"poNumber":{"nullable":true,"type":"string"}},"type":"object"},"endDate":{"format":"date","nullable":true,"type":"string"},"hasDeposits":{"default":false,"readOnly":true,"type":"boolean"},"id":{"readOnly":true,"type":"integer"},"rent":{"allOf":[{"properties":{"bankAccount":{"nullable":true,"type":"string"},"billableRent":{"description":"Total billable rent including sub-products","type":"number"},"bindingPeriod":{"maximum":36,"minimum":0,"nullable":true,"type":"integer"},"dueDay":{"enum":[1,5,10,15,20,25],"type":"number"},"duePeriod":{"enum":["advance","current","postpay"],"type":"string"},"invoicePeriod":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"rent":{"description":"Tenancy rent","type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancyRent","type":"object"}],"readOnly":true},"rentAdjustment":{"allOf":[{"nullable":true,"properties":{"CPIAdjustment":{"default":false,"type":"boolean"},"inProgress":{"default":false,"type":"boolean"},"lastCPIAdjustment":{"format":"date","type":"string"}},"title":"TenancyRentAdjustment","type":"object"}],"readOnly":true},"rentalObjectId":{"readOnly":true,"type":"integer"},"settings":{"properties":{"debt_collection":{"enum":["manual","automatic_debt_collection"],"nullable":false,"type":"string"}},"type":"object"},"startDate":{"format":"date","type":"string"},"status":{"default":"active","enum":["active","free","resigned","deleted","suspended","duplicate","paused"],"type":"string"},"subProducts":{"items":{"allOf":[{"properties":{"category":{"default":2,"type":"integer"},"effectiveDate":{"format":"date","nullable":true,"type":"string"},"invoiceInterval":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"name":{"type":"string"},"price":{"type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancySubProductBarebone","type":"object"}],"properties":{"id":{"type":"integer"}},"title":"TenancySubProduct","type":"object"},"readOnly":true,"type":"array"},"subscription":{"allOf":[{"properties":{"payment":{"properties":{"stripe":{"properties":{"paymentMethodId":{"type":"string"}},"type":"object"}},"type":"object"},"price":{"readOnly":true,"type":"number"},"priceCategory":{"enum":["currency","percentage","units"],"readOnly":true,"type":"string"},"subscriptionPlanId":{"type":"integer"},"subscriptionPlanName":{"readOnly":true,"type":"string"}},"title":"TenancySubscription","type":"object"}],"readOnly":true},"tenants":{"items":{"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"type":"string"},"fullName":{"type":"string"},"id":{"type":"string"},"organizationName":{"type":"string"},"organizationNumber":{"type":"string"},"type":{"default":"tenant","enum":["tenant","cotenant"],"type":"string"}},"title":"TenancyTenant","type":"object"},"readOnly":true,"type":"array"}},"title":"Tenancy","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {boolean} data.body.inProgress  Default: [0]
	 * @param {string} data.body.previousCPIAdjustment 
	 * @param {string} data.body.status  Enum: ['active', 'inactive']

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancyCPIAdjustment}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancyCPIAdjustment(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancyCPIAdjustment" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"inProgress":{"default":false,"readOnly":true,"type":"boolean"},"previousCPIAdjustment":{"format":"date","type":"string"},"status":{"enum":["active","inactive"],"type":"string"}},"title":"TenancyCPIAdjustment","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/CPIAdjustment', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} deposit_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.amount 
	 * @param {string} data.body.category  Enum: ['deposit_guarantee', 'easybank_deposit_account', 'other_deposit_guarantee', 'other_deposit_account', 'bank_guarantee', 'nav_guarantee', 'upsell_deposit_subscription', 'deposit_instabank', 'deposit_leabank', 'other'] Default: [other]
	 * @param {boolean} data.body.deletable  Default: [1]
	 * @param {object} data.body.fileMeta 
	 * @param {string} data.body.fileMeta.contentType 
	 * @param {string} data.body.fileMeta.fileName 
	 * @param {string} data.body.fileMeta.path 
	 * @param {string} data.body.fileToken 
	 * @param {string} data.body.id 
	 * @param {boolean} data.body.initiatedRelease  Default: [0]
	 * @param {object} data.body.meta 
	 * @param {string} data.body.status  Enum: ['new', 'initial', 'pending', 'active', 'ended', 'conflict'] Default: [initial]
	 * @param {object} data.body.termination 
	 * @param {number} data.body.termination.accepted If the termination has been accepted by tenant
	 * @param {number} data.body.termination.answered If the termination has been answered by tenant
	 * @param {number} data.body.termination.currentBalance 
	 * @param {number} data.body.termination.depositAmount The total sum of the deposit
	 * @param {number} data.body.termination.splitAmount If a sum of the deposit should be refunded to the landlord

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancyDeposit}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancyDeposit(landlord_id, tenancy_id, deposit_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancyDeposit" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"amount":{"type":"number"},"category":{"default":"other","enum":["deposit_guarantee","easybank_deposit_account","other_deposit_guarantee","other_deposit_account","bank_guarantee","nav_guarantee","upsell_deposit_subscription","deposit_instabank","deposit_leabank","other"],"type":"string"},"deletable":{"default":true,"readOnly":true,"type":"boolean"},"fileMeta":{"properties":{"contentType":{"type":"string"},"fileName":{"type":"string"},"path":{"type":"string"}},"readOnly":true,"type":"object"},"fileToken":{"type":"string"},"id":{"type":"string"},"initiatedRelease":{"default":false,"readOnly":true,"type":"boolean"},"meta":{"type":"object"},"status":{"default":"initial","enum":["new","initial","pending","active","ended","conflict"],"type":"string"},"termination":{"properties":{"accepted":{"description":"If the termination has been accepted by tenant","nullable":true,"type":"number"},"answered":{"description":"If the termination has been answered by tenant","nullable":true,"type":"number"},"currentBalance":{"nullable":true,"type":"number"},"depositAmount":{"description":"The total sum of the deposit","nullable":true,"type":"number"},"splitAmount":{"description":"If a sum of the deposit should be refunded to the landlord","nullable":true,"type":"number"}},"type":"object"}},"title":"TenancyDeposit","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/deposits/{deposit_id}', {
			landlord_id,
			tenancy_id,
			deposit_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.bankAccount 
	 * @param {number} data.body.billableRent Total billable rent including sub-products
	 * @param {integer} data.body.bindingPeriod 
	 * @param {number} data.body.dueDay  Enum: ['1', '5', '10', '15', '20', '25']
	 * @param {string} data.body.duePeriod  Enum: ['advance', 'current', 'postpay']
	 * @param {string} data.body.invoicePeriod  Enum: ['month', 'bimonth', 'quarter', 'biquarter', 'annual', 'semi-anual'] Default: [month]
	 * @param {number} data.body.rent Tenancy rent
	 * @param {integer} data.body.vat 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancyRent}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancyRent(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancyRent" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"bankAccount":{"nullable":true,"type":"string"},"billableRent":{"description":"Total billable rent including sub-products","type":"number"},"bindingPeriod":{"maximum":36,"minimum":0,"nullable":true,"type":"integer"},"dueDay":{"enum":["1","5","10","15","20","25"],"type":"number"},"duePeriod":{"enum":["advance","current","postpay"],"type":"string"},"invoicePeriod":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"rent":{"description":"Tenancy rent","type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancyRent","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/rent', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} subproduct_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.id 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancySubProduct}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancySubProduct(landlord_id, tenancy_id, subproduct_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancySubProduct" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"allOf":[{"properties":{"category":{"default":2,"type":"integer"},"effectiveDate":{"format":"date","nullable":true,"type":"string"},"invoiceInterval":{"default":"month","enum":["month","bimonth","quarter","biquarter","annual","semi-anual"],"type":"string"},"name":{"type":"string"},"price":{"type":"number"},"vat":{"maximum":100,"minimum":0,"type":"integer"}},"title":"TenancySubProductBarebone","type":"object"}],"properties":{"id":{"type":"integer"}},"title":"TenancySubProduct","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/subproducts/{subproduct_id}', {
			landlord_id,
			tenancy_id,
			subproduct_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {} tenant_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {object} data.body.address 
	 * @param {string} data.body.address.careOf If postal and C/O aka care of is required
	 * @param {string} data.body.address.countryCode ISO 3166-1 alpha-2 country code for postal address Default: [NO]
	 * @param {string} data.body.address.postalArea Postal area
	 * @param {string} data.body.address.postalRegion Postal region, like state or county. Not used in NO
	 * @param {string} data.body.address.postalZipcode Postal Zipcode
	 * @param {string} data.body.address.purpose Enumeration of the purpose of the address entry Enum: ['postal', 'billing', 'residential', 'business'] Default: [residential]
	 * @param {string} data.body.address.street Street name and number
	 * @param {string} data.body.cellPhone 
	 * @param {string} data.body.dateOfBirth 
	 * @param {string} data.body.email 
	 * @param {string} data.body.fullName 
	 * @param {string} data.body.id 
	 * @param {string} data.body.organizationName 
	 * @param {string} data.body.organizationNumber 
	 * @param {string} data.body.type  Enum: ['tenant', 'cotenant'] Default: [tenant]

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateLandlordTenancyTenant}
	 * @returns {Promise} A promise
	 */
	updateLandlordTenancyTenant(landlord_id, tenancy_id, tenant_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateLandlordTenancyTenant" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"address":{"properties":{"careOf":{"description":"If postal and C/O aka care of is required","nullable":true,"type":"string"},"countryCode":{"default":"NO","description":"ISO 3166-1 alpha-2 country code for postal address","nullable":true,"type":"string"},"postalArea":{"description":"Postal area","nullable":true,"type":"string"},"postalRegion":{"description":"Postal region, like state or county. Not used in NO","nullable":true,"type":"string"},"postalZipcode":{"description":"Postal Zipcode","nullable":true,"type":"string"},"purpose":{"default":"residential","description":"Enumeration of the purpose of the address entry","enum":["postal","billing","residential","business"],"type":"string"},"street":{"description":"Street name and number","nullable":true,"type":"string"}},"required":["street","postalArea","postalZipcode"],"type":"object"},"cellPhone":{"nullable":true,"type":"string"},"dateOfBirth":{"format":"date","nullable":true,"type":"string"},"email":{"type":"string"},"fullName":{"type":"string"},"id":{"type":"string"},"organizationName":{"type":"string"},"organizationNumber":{"type":"string"},"type":{"default":"tenant","enum":["tenant","cotenant"],"type":"string"}},"title":"TenancyTenant","type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlords/{landlord_id}/tenancies/{tenancy_id}/tenants/{tenant_id}', {
			landlord_id,
			tenancy_id,
			tenant_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.origin 
	 * @param {object} data.body.payment 
	 * @param {object} data.body.payment.stripe 
	 * @param {string} data.body.payment.stripe.paymentMethodId 
	 * @param {string} data.body.payment.type  Enum: ['stripe']
	 * @param {integer} data.body.subscriptionPlanId 

	 * @see {@link file:///./redoc-husleieapi.html#operation/updateTenancySubscription}
	 * @returns {Promise} A promise
	 */
	updateTenancySubscription(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "updateTenancySubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"origin":{"pattern":"^[a-z0-9_]+$","type":"string"},"payment":{"properties":{"stripe":{"properties":{"paymentMethodId":{"type":"string"}},"type":"object"},"type":{"enum":["stripe"],"type":"string"}},"required":["type","stripe"],"type":"object"},"subscriptionPlanId":{"type":"integer"}},"required":["subscriptionPlanId"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/subscription', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id
	 * @param {} tenancy_id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.origin 
	 * @param {integer} data.body.subscriptionPlanId 

	 * @see {@link file:///./redoc-husleieapi.html#operation/upgradeTenancySubscription}
	 * @returns {Promise} A promise
	 */
	upgradeTenancySubscription(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "upgradeTenancySubscription" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"origin":{"pattern":"^[a-z0-9_]+$","type":"string"},"subscriptionPlanId":{"type":"integer"}},"required":["subscriptionPlanId"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/subscription/upgrade', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * TenancyAccounting
 * @extends HusleieAPI2
 */
export class TenancyAccountingApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "TenancyAccounting"...');

	}


	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteOneLandlordTenancyAccountingOrder}
	 * @returns {Promise} A promise
	 */
	deleteOneLandlordTenancyAccountingOrder(landlord_id, tenancy_id, order_id, responseType = 'json') {

		this.logger('Calling API function "deleteOneLandlordTenancyAccountingOrder" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @param {} payment_id The accounting payment id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteOneLandlordTenancyAccountingOrderDirectPayment}
	 * @returns {Promise} A promise
	 */
	deleteOneLandlordTenancyAccountingOrderDirectPayment(landlord_id, tenancy_id, order_id, payment_id, responseType = 'json') {

		this.logger('Calling API function "deleteOneLandlordTenancyAccountingOrderDirectPayment" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/direct_payment/{payment_id}', {
			landlord_id,
			tenancy_id,
			order_id,
			payment_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteOneLandlordTenancyAccountingOrderLink}
	 * @returns {Promise} A promise
	 */
	deleteOneLandlordTenancyAccountingOrderLink(landlord_id, tenancy_id, order_id, responseType = 'json') {

		this.logger('Calling API function "deleteOneLandlordTenancyAccountingOrderLink" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/link', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @param {} row_id The order row id
	 * @see {@link file:///./redoc-husleieapi.html#operation/deleteOneLandlordTenancyAccountingOrderRowById}
	 * @returns {Promise} A promise
	 */
	deleteOneLandlordTenancyAccountingOrderRowById(landlord_id, tenancy_id, order_id, row_id, responseType = 'json') {

		this.logger('Calling API function "deleteOneLandlordTenancyAccountingOrderRowById" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/rows/{row_id}', {
			landlord_id,
			tenancy_id,
			order_id,
			row_id,
		});

		this.logger(`Sending DELETE request to ${path}...`);

		return this.retry( () => {
			return this.axios.delete(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.year {}Return only the specified year
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyAccountingLedger}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyAccountingLedger(landlord_id, tenancy_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyAccountingLedger" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/ledger', {
			landlord_id,
			tenancy_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.limit {}Limit amount of items returned, if 0 everything will be returned
	 * @param data.query.page {}Which page to display, only respected if limit has been specified
	 * @param data.query.year {}Return only the specified year
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyAccountingOrders}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyAccountingOrders(landlord_id, tenancy_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyAccountingOrders" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders', {
			landlord_id,
			tenancy_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.year {}Return only the specified year
	 * @see {@link file:///./redoc-husleieapi.html#operation/getLandlordTenancyAccountingSummary}
	 * @returns {Promise} A promise
	 */
	getLandlordTenancyAccountingSummary(landlord_id, tenancy_id, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getLandlordTenancyAccountingSummary" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/summary', {
			landlord_id,
			tenancy_id,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @see {@link file:///./redoc-husleieapi.html#operation/getOneLandlordTenancyAccountingOrder}
	 * @returns {Promise} A promise
	 */
	getOneLandlordTenancyAccountingOrder(landlord_id, tenancy_id, order_id, responseType = 'json') {

		this.logger('Calling API function "getOneLandlordTenancyAccountingOrder" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @see {@link file:///./redoc-husleieapi.html#operation/getOneLandlordTenancyAccountingOrderPdfUri}
	 * @returns {Promise} A promise
	 */
	getOneLandlordTenancyAccountingOrderPdfUri(landlord_id, tenancy_id, order_id, responseType = 'json') {

		this.logger('Calling API function "getOneLandlordTenancyAccountingOrderPdfUri" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/pdfuri', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.dueDate The due date of the order
	 * @param {string} data.body.period The period in text / order title this order is for
	 * @param {} data.body.rows 

	 * @see {@link file:///./redoc-husleieapi.html#operation/patchOneLandlordTenancyAccountingOrder}
	 * @returns {Promise} A promise
	 */
	patchOneLandlordTenancyAccountingOrder(landlord_id, tenancy_id, order_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "patchOneLandlordTenancyAccountingOrder" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"dueDate":{"description":"The due date of the order","format":"date","nullable":true,"type":"string"},"period":{"description":"The period in text / order title this order is for","nullable":true,"type":"string"},"rows":{"oneOf":[{"properties":{"amount":{"description":"The amount to be invoiced","format":"float","nullable":false,"type":"number"},"categoryId":{"description":"Category ID of the row","nullable":false,"type":"integer"},"id":{"description":"The unique ID of a row","nullable":true,"type":"string"},"text":{"description":"The order row text description","minLength":1,"nullable":false,"type":"string"},"vat":{"description":"The percentage of VAT to be added to the amount","maximum":100,"minimum":0,"type":"integer"}},"type":"object"},{"items":{"properties":{"amount":{"description":"The amount to be invoiced","format":"float","nullable":false,"type":"number"},"categoryId":{"description":"Category ID of the row","nullable":false,"type":"integer"},"id":{"description":"The unique ID of a row","nullable":true,"type":"string"},"text":{"description":"The order row text description","minLength":1,"nullable":false,"type":"string"},"vat":{"description":"The percentage of VAT to be added to the amount","maximum":100,"minimum":0,"type":"integer"}},"type":"object"},"type":"array"}]}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending PATCH request to ${path}...`);

		return this.retry( () => {
			return this.axios.patch(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @param {object} data Object containing information about the request
	 * @param data.body {array} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordTenancyAccountingOrderRows}
	 * @returns {Promise} A promise
	 */
	postLandlordTenancyAccountingOrderRows(landlord_id, tenancy_id, order_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordTenancyAccountingOrderRows" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"items":{"properties":{"amount":{"description":"The amount to be invoiced","format":"float","nullable":false,"type":"number"},"categoryId":{"description":"Category ID of the row","nullable":false,"type":"integer"},"id":{"description":"The unique ID of a row","nullable":true,"type":"string"},"text":{"description":"The order row text description","minLength":1,"nullable":false,"type":"string"},"vat":{"description":"The percentage of VAT to be added to the amount","maximum":100,"minimum":0,"type":"integer"}},"type":"object"},"type":"array"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/rows', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {integer} data.body.creditNotedOrderId ID of order that the credit note is for
	 * @param {string} data.body.dueDate The due date of the order
	 * @param {string} data.body.period The period in text / order title this order is for
	 * @param {array} data.body.rows 
	 * @param {number} data.body.rows[].amount The amount to be invoiced
	 * @param {integer} data.body.rows[].categoryId Category ID of the row
	 * @param {string} data.body.rows[].id The unique ID of a row
	 * @param {string} data.body.rows[].text The order row text description
	 * @param {integer} data.body.rows[].vat The percentage of VAT to be added to the amount
	 * @param {boolean} data.body.sendCopy Boolean flag to indicate if a copy should be sent to the tenant
	 * @param {string} data.body.typeOf What type of invoice this is Enum: ['rentInvoice', 'otherInvoice', 'creditNote']

	 * @see {@link file:///./redoc-husleieapi.html#operation/postLandlordTenancyAccountingOrders}
	 * @returns {Promise} A promise
	 */
	postLandlordTenancyAccountingOrders(landlord_id, tenancy_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postLandlordTenancyAccountingOrders" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"creditNotedOrderId":{"description":"ID of order that the credit note is for","nullable":true,"type":"integer"},"dueDate":{"description":"The due date of the order","format":"date","nullable":false,"type":"string"},"period":{"description":"The period in text / order title this order is for","nullable":true,"type":"string"},"rows":{"items":{"properties":{"amount":{"description":"The amount to be invoiced","format":"float","nullable":false,"type":"number"},"categoryId":{"description":"Category ID of the row","nullable":false,"type":"integer"},"id":{"description":"The unique ID of a row","nullable":true,"type":"string"},"text":{"description":"The order row text description","minLength":1,"nullable":false,"type":"string"},"vat":{"description":"The percentage of VAT to be added to the amount","maximum":100,"minimum":0,"type":"integer"}},"type":"object"},"type":"array"},"sendCopy":{"description":"Boolean flag to indicate if a copy should be sent to the tenant","nullable":false,"type":"boolean"},"typeOf":{"description":"What type of invoice this is","enum":["rentInvoice","otherInvoice","creditNote"],"nullable":false,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders', {
			landlord_id,
			tenancy_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @see {@link file:///./redoc-husleieapi.html#operation/postOneLandlordTenancyAccountingOrder}
	 * @returns {Promise} A promise
	 */
	postOneLandlordTenancyAccountingOrder(landlord_id, tenancy_id, order_id, responseType = 'json') {

		this.logger('Calling API function "postOneLandlordTenancyAccountingOrder" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.amount 
	 * @param {string} data.body.date 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postOneLandlordTenancyAccountingOrderDirectPayment}
	 * @returns {Promise} A promise
	 */
	postOneLandlordTenancyAccountingOrderDirectPayment(landlord_id, tenancy_id, order_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postOneLandlordTenancyAccountingOrderDirectPayment" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"amount":{"format":"float","nullable":false,"type":"number"},"date":{"format":"date","nullable":false,"type":"string"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/direct_payment', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postOneLandlordTenancyAccountingOrderEmailCopy}
	 * @returns {Promise} A promise
	 */
	postOneLandlordTenancyAccountingOrderEmailCopy(landlord_id, tenancy_id, order_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postOneLandlordTenancyAccountingOrderEmailCopy" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/email_copy', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @param {} parent_order_id The order to return
	 * @see {@link file:///./redoc-husleieapi.html#operation/postOneLandlordTenancyAccountingOrderLink}
	 * @returns {Promise} A promise
	 */
	postOneLandlordTenancyAccountingOrderLink(landlord_id, tenancy_id, order_id, parent_order_id, responseType = 'json') {

		this.logger('Calling API function "postOneLandlordTenancyAccountingOrderLink" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/link/{parent_order_id}', {
			landlord_id,
			tenancy_id,
			order_id,
			parent_order_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @param {} row_id The order row id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {number} data.body.amount The amount to be invoiced
	 * @param {integer} data.body.categoryId Category ID of the row
	 * @param {string} data.body.id The unique ID of a row
	 * @param {string} data.body.text The order row text description
	 * @param {integer} data.body.vat The percentage of VAT to be added to the amount

	 * @see {@link file:///./redoc-husleieapi.html#operation/postOneLandlordTenancyAccountingOrderRowById}
	 * @returns {Promise} A promise
	 */
	postOneLandlordTenancyAccountingOrderRowById(landlord_id, tenancy_id, order_id, row_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postOneLandlordTenancyAccountingOrderRowById" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"amount":{"description":"The amount to be invoiced","format":"float","nullable":false,"type":"number"},"categoryId":{"description":"Category ID of the row","nullable":false,"type":"integer"},"id":{"description":"The unique ID of a row","nullable":true,"type":"string"},"text":{"description":"The order row text description","minLength":1,"nullable":false,"type":"string"},"vat":{"description":"The percentage of VAT to be added to the amount","maximum":100,"minimum":0,"type":"integer"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/rows/{row_id}', {
			landlord_id,
			tenancy_id,
			order_id,
			row_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} order_id The order to return
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {boolean} data.body.stopAllReminders 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postOneLandlordTenancyAccountingOrderToggleReminder}
	 * @returns {Promise} A promise
	 */
	postOneLandlordTenancyAccountingOrderToggleReminder(landlord_id, tenancy_id, order_id, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "postOneLandlordTenancyAccountingOrderToggleReminder" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"stopAllReminders":{"nullable":false,"type":"boolean"}},"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/orders/{order_id}/toggle_reminder', {
			landlord_id,
			tenancy_id,
			order_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {} landlord_id Required Landlord ID
	 * @param {} tenancy_id Required Tenancy ID
	 * @param {} payment_id The accounting payment id
	 * @param {} parent_order_id The order to return
	 * @see {@link file:///./redoc-husleieapi.html#operation/postOneLandlordTenancyAccountingPaymentLink}
	 * @returns {Promise} A promise
	 */
	postOneLandlordTenancyAccountingPaymentLink(landlord_id, tenancy_id, payment_id, parent_order_id, responseType = 'json') {

		this.logger('Calling API function "postOneLandlordTenancyAccountingPaymentLink" with ' + arguments.length + ' argument(s): ', ...arguments);

			let body = {};

		let path = this.expandPath('/landlord/{landlord_id}/tenancies/{tenancy_id}/accounting/payments/{payment_id}/link/{parent_order_id}', {
			landlord_id,
			tenancy_id,
			payment_id,
			parent_order_id,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Token
 * @extends HusleieAPI2
 */
export class TokenApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Token"...');

	}


	/**
	 * Authenticate with HTTP BasicAuth, and a JWT token will be returned. The JWT token is required to access the remaining API endpoints.
	 * @see {@link file:///./redoc-husleieapi.html#operation/getAuthenticationToken}
	 * @returns {Promise} A promise
	 */
	getAuthenticationToken(responseType = 'json') {

		this.logger('Calling API function "getAuthenticationToken" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/token';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * This endpoint triggers a email with a timed login link to the user
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.email 

	 * @see {@link file:///./redoc-husleieapi.html#operation/sendLoginEmailToken}
	 * @returns {Promise} A promise
	 */
	sendLoginEmailToken({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "sendLoginEmailToken" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"email":{"format":"email","type":"string"}},"required":["email"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/auth/email_token';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Tool
 * @extends HusleieAPI2
 */
export class ToolApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Tool"...');

	}


	/**
	 *
	 * @param {} bankaccountprefix
	 * @see {@link file:///./redoc-husleieapi.html#operation/lookupBankInformationByBankAccountPrefix}
	 * @returns {Promise} A promise
	 */
	lookupBankInformationByBankAccountPrefix(bankaccountprefix, responseType = 'json') {

		this.logger('Calling API function "lookupBankInformationByBankAccountPrefix" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/tool/bank_lookup/{bankaccountprefix}', {
			bankaccountprefix,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 *
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/postStatsReport}
	 * @returns {Promise} A promise
	 */
	postStatsReport({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "postStatsReport" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"oneOf":[{"items":{"properties":{"name":{"example":"api.request.success","type":"string"},"sampleRate":{"example":0.5,"type":"number"},"type":{"enum":["counter","timing","gauge"],"example":"counter","type":"string"},"value":{"example":100,"type":"number"}},"type":"object"},"type":"array"},{"properties":{"name":{"example":"api.request.success","type":"string"},"sampleRate":{"example":0.5,"type":"number"},"type":{"enum":["counter","timing","gauge"],"example":"counter","type":"string"},"value":{"example":100,"type":"number"}},"type":"object"}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/tool/stats';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Translation
 * @extends HusleieAPI2
 */
export class TranslationApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Translation"...');

	}


	/**
	 * If successfull, this will return JSON object containing translations
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.projects {}Required Project or Projects
	 * @param data.query.languageCode {}Which country should we try to fetch translation for
	 * @see {@link file:///./redoc-husleieapi.html#operation/getTranslations}
	 * @returns {Promise} A promise
	 */
	getTranslations({ query } = {}, responseType = 'json') {

		this.logger('Calling API function "getTranslations" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/translations';

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * Upsell
 * @extends HusleieAPI2
 */
export class UpsellApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "Upsell"...');

	}


	/**
	 * Create an upsell session
	 * @param {} upsell_type Required upsell type
	 * @param {object} data Object containing information about the request
	 * @param data.body {} 

	 * @see {@link file:///./redoc-husleieapi.html#operation/createUpsell}
	 * @returns {Promise} A promise
	 */
	createUpsell(upsell_type, { body } = {}, responseType = 'json') {

		this.logger('Calling API function "createUpsell" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"oneOf":[{"oneOf":[{"properties":{"plan":{"properties":{"id":{"type":"integer"}},"type":"object"},"token":{"type":"string"}},"required":["token"],"type":"object"}]}]};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = this.expandPath('/upsell/{upsell_type}', {
			upsell_type,
		});

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Get upsell status
	 * @param {} upsell_type Required upsell type
	 * @param {} upsell_id Required upsell id
	 * @see {@link file:///./redoc-husleieapi.html#operation/getUpsellStatus}
	 * @returns {Promise} A promise
	 */
	getUpsellStatus(upsell_type, upsell_id, responseType = 'json') {

		this.logger('Calling API function "getUpsellStatus" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/upsell/{upsell_type}/{upsell_id}', {
			upsell_type,
			upsell_id,
		});

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}

/**
 * User
 * @extends HusleieAPI2
 */
export class UserApi extends HusleieAPI2 {

	constructor(...args) {
		super( ...args );

		this.logger('Created new instance of class "User"...');

	}


	/**
	 * Return the identity of the user
	 * @see {@link file:///./redoc-husleieapi.html#operation/getUserIdentity}
	 * @returns {Promise} A promise
	 */
	getUserIdentity(responseType = 'json') {

		this.logger('Calling API function "getUserIdentity" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = '/user/identity';

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Return all sessions of the requested type of the user
	 * @param {} type Required session type
	 * @param {object} data Object containing information about the request
	 * @param data.query {object}
	 * @param data.query.status {}
	 * @param data.query.orderBy {}
	 * @param data.query.page {}
	 * @param data.query.pageSize {}
	 * @see {@link file:///./redoc-husleieapi.html#operation/getUserSessions}
	 * @returns {Promise} A promise
	 */
	getUserSessions(type, { query } = {}, responseType = 'json') {

		this.logger('Calling API function "getUserSessions" with ' + arguments.length + ' argument(s): ', ...arguments);

		let path = this.expandPath('/user/sessions/{type}', {
			type,
		});

		if (typeof query !== 'undefined') {
			path += '?' + qs.stringify(query);
		}

		this.logger(`Sending GET request to ${path}...`);

		return this.retry( () => {
			return this.axios.get(path, { responseType }).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

	/**
	 * Set the identity of the current user using a identification sessions id
	 * @param {object} data Object containing information about the request
	 * @param data.body {object} 
	 * @param {string} data.body.identificationSessionId 

	 * @see {@link file:///./redoc-husleieapi.html#operation/setUserIdentity}
	 * @returns {Promise} A promise
	 */
	setUserIdentity({ body } = {}, responseType = 'json') {

		this.logger('Calling API function "setUserIdentity" with ' + arguments.length + ' argument(s): ', ...arguments);

		/* - To be implemented / enabled in the future... (Famous last words..)
		const schema = {"properties":{"identificationSessionId":{"type":"string"}},"required":["identificationSessionId"],"type":"object"};
		if (!this.validateSchema(body, schema)) {
			return Promise.reject('Schema validation: Invalid body');
		}
		*/
		let path = '/user/identity';

		this.logger(`Sending POST request to ${path}...`);

		return this.retry( () => {
			return this.axios.post(path, body).then((res) => {
				this.logger(`Got response from ${path}:`, res);
				return res.data;
			});
		});

	}

}


window.___api_logging = false;

try {
	if ('localStorage' in window) {
		window.___api_logging = (window.localStorage.getItem('api_logging')) === 'true' ? true : false;
	}
} catch(e) {
	// eslint-disable-next-line no-console
	console.error(e);
}

window.api_logging = (s) => {
	if (s === undefined)
		return window.___api_logging;

	window.___api_logging = s ? true : false;

	try {
		if ('localStorage' in window) {
			window.localStorage.setItem('api_logging', window.___api_logging);
		}
		// eslint-disable-next-line no-console
		console.log('HusleieAPI2: Logging set to ', window.___api_logging);
	} catch(e) {
		// eslint-disable-next-line no-console
		console.error(e);
	}
};
